import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';

import StatTile from 'shared/components/Dashboard/StatTile';
import MetricCard from 'shared/components/Dashboard/MetricCard';
import AppChart, {
  ChartSeries,
  ChartTool,
  ScatterGroup,
} from 'shared/components/Chart/AppChart';
import ReportDataTable, {
  ReportColumn,
} from 'shared/components/Table/ReportDataTable';
import HeatmapTable from '../../shared/components/Heatmap/HeatmapTable';
import DrillBreadcrumb from 'shared/components/Dashboard/DrillBreadcrumb';
import {
  buildDrilldown,
  DrillField,
  Filters,
  EMPTY_FILTERS,
  drillHint,
  drillLevelLabel,
  drillSearchPlaceholder,
  scopeLabel,
  withUdise,
} from 'shared/json/drilldown';
import {
  getAcademicData,
  districtAcademicSummary,
  ClassBand,
  TREND,
  DISTRICT_TRENDS,
  COMPARISON_HEATMAP,
  ALARMING_KPIS,
  ALERTS_BY_CATEGORY,
  ALERT_DETAILS,
  RISK_POINTS,
  CAUTION_CHECKS,
  CAUTION_TOTAL_SCHOOLS,
  CAUTION_SCHOOLS,
  AcademicData,
} from 'shared/json/academicScores';
import {
  useGetAcademicComplianceCardsQuery,
  useGetAcademicComplianceListMultiQuery,
  useGetAcademicSubjectComparisonQuery,
  useGetAcademicExamCompareQuery,
  useGetAcademicExamSittingsQuery,
  useGetAcademicExamDistrictImprovementQuery,
  useGetAcademicSummaryQuery,
  useGetAcademicSummaryStreamQuery,
  useGetAcademicExamClasswiseQuery,
  useGetAcademicStreamComparisonQuery,
  useGetAcademicDistrictsQuery,
  useGetAcademicDistrictsStreamQuery,
  useGetAcademicTrendCombinedQuery,
  useGetCommonMastersQuery,
  useGetAllDistrictListQuery,
  AcademicExamCompareSubject,
  AcademicSummaryResponse,
  AcademicSummarySubject,
  AcademicExamProgressionRow,
  AcademicClassWiseRow,
  AcademicStreamComparisonResponse,
  AcademicDistrictRow,
  AcademicTrendPoint,
} from './Dashboard.service';

import './AcademicScores.scss';

// API numbers can arrive as strings ("0"); coerce to a number.
const n0 = (v: unknown) => Number(v) || 0;
const toLakh = (n: number) => Math.round((n / 1e5) * 10) / 10;

// Single scope note shown under every Academic Scores sub-tab's control row.
const ACADEMIC_SCOPE_NOTE =
  'Government, Partially Aided & Fully Aided schools only. Class 10 & 12 = board exam (Annual only); classes 6-10 sit 5 core subjects, 11-12 are stream based.';
const EXAM_PERIODS: [number, string][] = [
  [1, 'Quarterly'],
  [2, 'Half Yearly'],
  [3, 'Annual'],
];
// Subject master (subject_id → name): Class 6-10 Numbers + the exam APIs.
const SUBJECT_NAMES: Record<string, string> = {
  '3': 'Mathematics',
  '7': 'Science',
  '8': 'Social Science',
  '46': 'English',
  '48': 'Tamil',
};
const subjectName = (s: AcademicSummarySubject) =>
  s.subject_name || `Subject ${s.subject_id}`;
// Class 6-10 names come from the subject master; 11-12 keeps the API name.
const bandSubjectName =
  (band: ClassBand) =>
  (s: AcademicSummarySubject): string =>
    (band === '6-10' ? SUBJECT_NAMES[String(s.subject_id)] : undefined) ??
    subjectName(s);

// Drop subjects with no marks (average_mark 0) straight off the
// academicsummary / academicsummarystream response, so every Numbers section
// (radar, mark bands, subject detail, strongest/weakest) sees the same list.
const withMarkedSubjects = (
  r: AcademicSummaryResponse['result'] | undefined
): AcademicSummaryResponse['result'] | undefined =>
  r && {
    ...r,
    subjects: (r.subjects ?? []).filter((s) => n0(s.average_mark) !== 0),
  };

// Map the live academic APIs (academicsummary / academicsummarystream +
// academicexamclasswise + academicstreamcomparison) onto the AcademicData
// shape the Numbers renderer consumes. Returns null until the API responds.
function buildAcademicFromApi(
  band: ClassBand,
  sel: AcademicSummaryResponse['result'] | undefined,
  state: AcademicSummaryResponse['result'] | undefined,
  progression: AcademicExamProgressionRow[] | undefined,
  classWiseRows: AcademicClassWiseRow[] | undefined,
  streams:
    AcademicStreamComparisonResponse['result']['stream_comparison'] | undefined
): AcademicData | null {
  if (!sel?.cards) return null;
  const nameOf = bandSubjectName(band);
  const subjects = sel.subjects ?? [];
  const labels = subjects.map(nameOf);
  const stateById = new Map(
    (state?.subjects ?? []).map((s) => [String(s.subject_id), s])
  );

  const subjectDetail = subjects.map((s) => ({
    subject: nameOf(s),
    averageMark: n0(s.average_mark),
    passPct: `${n0(s.pass_pct)}%`,
    below35: n0(s.below_35),
    band3560: n0(s.band_35_60),
    band6180: n0(s.band_61_80),
    above80: n0(s.above_80),
    absentees: n0(s.absentees),
  }));

  // Store original unscaled numbers for table display
  const originalSubjectDetail = subjects.map((s) => ({
    subject: nameOf(s),
    averageMark: n0(s.average_mark),
    passPct: `${n0(s.pass_pct)}%`,
    below35: n0(s.below_35),
    band3560: n0(s.band_35_60),
    band6180: n0(s.band_61_80),
    above80: n0(s.above_80),
    absentees: n0(s.absentees),
  }));

  // Strongest / weakest by average mark (subjects are pre-filtered to
  // those with marks — see withMarkedSubjects).
  const ranked = [...subjects].sort(
    (a, b) => n0(b.average_mark) - n0(a.average_mark)
  );
  const strongest = ranked[0];
  const weakest = ranked[ranked.length - 1];

  // Lakhs only make sense at state / district scale.
  const maxBand = Math.max(
    0,
    ...subjectDetail.map((r) => r.below35 + r.band3560 + r.band6180 + r.above80)
  );
  const inLakh = maxBand >= 1e5;
  const scale = (v: number) => (inLakh ? toLakh(v) : v);

  //console.log('Debug - Original API data:', {
  //   first_subject: subjects[0],
  //   originalSubjectDetail: originalSubjectDetail[0],
  //   inLakh: inLakh,
  // });

  const progById = new Map(
    (progression ?? []).map((r) => [Number(r.exam_type_id), r])
  );
  const classes = band === '6-10' ? ['6', '7', '8', '9', '10'] : ['11', '12'];
  const classById = new Map(
    (classWiseRows ?? []).map((r) => [String(r.class_id), r])
  );

  const bandLabel =
    band === '6-10'
      ? 'Core Subjects (Class 6-10)'
      : 'Higher Secondary (Class 11 & 12)';
  const cards = sel.cards;

  return {
    scope: ACADEMIC_SCOPE_NOTE,
    subjectCount: subjects.length,
    subjectCountLabel: `${bandLabel} · ${subjects.length} subjects`,
    profileBadge: `${bandLabel} · ${subjects.length} subjects`,
    markBandTitle: `Mark Band Distribution — ${bandLabel}`,
    subjectDetailTitle: `Subject Detail — ${bandLabel}`,
    kpis: {
      averageMark: n0(cards.average_mark),
      passPercentage: n0(cards.pass_percentage),
      compliance: n0(cards.compliance),
    },
    metrics: {
      studentsExpected: n0(cards.students_expected),
      marksEntered: n0(cards.marks_entered),
      strongestSubject: {
        name: strongest ? nameOf(strongest) : '-',
        value: n0(strongest?.average_mark),
      },
      weakestSubject: {
        name: weakest ? nameOf(weakest) : '-',
        value: n0(weakest?.average_mark),
      },
      below35Pct: n0(cards.below_35_pct),
    },
    // Radar is only legible with a handful of axes. When there are many
    // subjects (e.g. 42 at Higher Secondary / streams), show just the most
    // meaningful ones — the strongest few and the weakest few by average mark
    // — so the profile stays readable instead of an unreadable 42-spoke web.
    // The full list still appears in the Mark Band chart and Subject Detail
    // table below.
    subjectProfile: (() => {
      const RADAR_MAX = 10;
      const avgMarkById = (id: unknown) =>
        n0(stateById.get(String(id))?.average_mark);

      let profileSubjects = subjects;
      if (subjects.length > RADAR_MAX) {
        const half = Math.ceil(RADAR_MAX / 2);
        const byMark = [...subjects].sort(
          (a, b) => n0(b.average_mark) - n0(a.average_mark)
        );
        const top = byMark.slice(0, half); // strongest
        const bottom = byMark.slice(-half); // weakest
        const picked = [...top, ...bottom];
        // De-dupe (small lists can overlap) and keep the original subject order
        // so the radar reads consistently.
        const pickedIds = new Set(picked.map((s) => String(s.subject_id)));
        profileSubjects = subjects.filter((s) =>
          pickedIds.has(String(s.subject_id))
        );
      }

      return {
        labels: profileSubjects.map(nameOf),
        state: profileSubjects.map((s) => avgMarkById(s.subject_id)),
        selected: profileSubjects.map((s) => n0(s.average_mark)),
      };
    })(),
    markBand: {
      subjects: labels,
      below35: subjectDetail.map((r) => scale(r.below35)),
      band35_60: subjectDetail.map((r) => scale(r.band3560)),
      band61_80: subjectDetail.map((r) => scale(r.band6180)),
      above80: subjectDetail.map((r) => scale(r.above80)),
      // ORIGINAL (unscaled) band counts — the chart plots the lakh-scaled
      // arrays above for a readable Y axis, but the Table View must show the
      // real student counts, so pass these through as each series' tableData.
      below35Raw: subjectDetail.map((r) => r.below35),
      band35_60Raw: subjectDetail.map((r) => r.band3560),
      band61_80Raw: subjectDetail.map((r) => r.band6180),
      above80Raw: subjectDetail.map((r) => r.above80),
      // Pass % / Average Mark are 0–100 values, not lakh-scaled band counts.
      // passPct is a "82%" string, so parse it; avgMark is `averageMark`.
      passPct: subjectDetail.map((r) => parseFloat(r.passPct) || 0),
      avgMark: subjectDetail.map((r) => r.averageMark),
    },
    markBandInLakh: inLakh,
    examProgression: {
      periods: EXAM_PERIODS.map(([, l]) => l),
      avgMark: EXAM_PERIODS.map(([id]) => n0(progById.get(id)?.average_mark)),
      passPct: EXAM_PERIODS.map(([id]) => n0(progById.get(id)?.pass_pct)),
      compliance: EXAM_PERIODS.map(([id]) => n0(progById.get(id)?.compliance)),
    },
    classWise: {
      classes,
      avgMark: classes.map((c) => n0(classById.get(c)?.average_mark)),
      passPct: classes.map((c) => n0(classById.get(c)?.pass_pct)),
      boardClasses: band === '6-10' ? ['10'] : ['12'],
    },
    ...(band === '11-12' && streams?.length
      ? {
          streamPerf: {
            streams: streams.map((s) => s.stream_name || `${s.stream_id}`),
            avgMark: streams.map((s) => n0(s.average_mark)),
            passPct: streams.map((s) => n0(s.pass_pct)),
          },
        }
      : {}),
    subjectDetail,
    originalSubjectDetail,
  };
}

// Drill-down table row (academicdistricts / academicdistrictsstream).
const toSummaryRow = (band: ClassBand) => (r: AcademicDistrictRow) => {
  // Class 6-10: weakest subject name from the subject master.
  const weakestName =
    (band === '6-10' && r.weakest_subject_id != null
      ? SUBJECT_NAMES[String(r.weakest_subject_id)]
      : undefined) ?? r.weakest_subject_name;
  return {
    name: r.name,
    avgMark: n0(r.avg_mark),
    passPct: `${n0(r.pass_pct)}%`,
    compliance: `${n0(r.compliance)}%`,
    weakestSubject: weakestName
      ? `${weakestName} (${n0(r.weakest_subject_avg)})`
      : '-',
  };
};

// Trend tab: value of the chosen metric on one yearly point.
const trendValue = (p: AcademicTrendPoint | undefined, metric: string) =>
  n0(
    metric === 'pass'
      ? p?.pass_pct
      : metric === 'compliance'
        ? p?.compliance
        : p?.average_mark
  );
// Academic caution checks -> compliance list flag + tile presentation.
const ACADEMIC_CAUTION_CARDS = [
  {
    key: 'zero_mark_entry_compliance',
    flag: 1,
    label: 'Zero Mark Entry Compliance',
    icon: 'pi pi-file-excel',
    accent: '#c0392b',
    meaning: 'Schools yet to meet mark-entry compliance',
  },
  {
    key: 'mark_entry_under_25',
    flag: 2,
    label: 'Mark Entry Under 25%',
    icon: 'pi pi-percentage',
    accent: '#e8a33d',
    meaning: 'Mark entry below 25% of expected',
  },
] as const;
// Every caution band, requested in a single call so tile switching is free.
const ACADEMIC_CAUTION_FLAGS = ACADEMIC_CAUTION_CARDS.map((c) => c.flag).join(
  ','
);
export interface AcademicScoresProps {
  /** Academic year shown in the control rows + section titles. */
  year?: string;
  /** Academic year id — drives the dashboardsummary (component_id=2) call. */
  acdYr?: number | string | null;
  /** Header geography ids (scope the aggregation). */
  districtId?: number | string | null;
  blockId?: number | string | null;
  schoolId?: number | string | null;
  /** Active sub-tab from the dashboard header. */
  activeSubTab?: string;
  /** Header-selected district (drives the summary drill-down). */
  district?: string | null;
  /** Header-selected block. */
  block?: string | null;
  /** Header-selected school. */
  school?: string | null;
  /** Management / Category / School-Type filter selection. */
  filters?: Filters;
  /** Clear/return to the district level. */
  onDistrictChange?: (value: string) => void;
  /** Clear/return to the block level. */
  onBlockChange?: (value: string) => void;
  /** Select/clear a school. */
  onSchoolChange?: (value: string) => void;
  /**
   * Focus the dashboard on one school, setting District → Block → School
   * together. Used by the Caution flagged-school table, whose rows are
   * already at school level and so cannot drill any deeper.
   */
  onGeoSelect?: (geo: {
    district?: string | null;
    block?: string | null;
    school?: string | null;
  }) => void;
}

const fmtIndian = (n: number) => n.toLocaleString('en-IN');

const MARK_BAND_COLORS = {
  below35: '#e4572e',
  band35_60: '#f0a02c',
  band61_80: '#8bc34a',
  above80: '#2e7d32',
};

const RISK_COLORS: Record<string, string> = {
  critical: '#c0392b',
  'under-reporting': '#7e57c2',
  'under-performing': '#e8a33d',
  healthy: '#5cb85c',
};

const RISK_LABELS: Record<string, string> = {
  critical: 'Critical',
  'under-reporting': 'Under-reporting',
  'under-performing': 'Under-performing',
  healthy: 'Healthy',
};

const chartToolsNoTable: ChartTool[] = [
  { icon: 'pi pi-refresh', label: 'Refresh' },
  { icon: 'pi pi-download', label: 'Download' },
];

const metricOptions = [
  { label: 'Average Mark', value: 'avg' },
  { label: 'Pass %', value: 'pass' },
  { label: 'Compliance', value: 'compliance' },
];

// ── Between Exams (comparison tab) ──
const EXAM_TYPES = [
  { label: 'Quarterly', value: 1 },
  { label: 'Half Yearly', value: 2 },
  { label: 'Annual', value: 3 },
];
const EXAM_CLASSES = [6, 7, 8, 9, 10, 11, 12].map((c) => ({
  label: `Class ${c}`,
  value: c,
}));
const examName = (id: number | string) =>
  EXAM_TYPES.find((e) => e.value === Number(id))?.label ?? `Exam ${id}`;
// Class 10 & 12 Annual are state board exams; everything else is EMIS entry.
const isBoardExam = (cls: number | string, exam: number | string) =>
  (Number(cls) === 10 || Number(cls) === 12) && Number(exam) === 3;
const sittingLabel = (cls: number | string, exam: number | string) =>
  `${examName(exam)} - Class ${cls}`;
// API numbers can arrive as strings ("0"); coerce + round to 1 decimal.
const num = (v: unknown) => Number(v) || 0;
const r1 = (v: number) => Math.round(v * 10) / 10;
const signed = (v: number, suffix = '') =>
  `${v > 0 ? '+' : ''}${r1(v)}${suffix}`;
const toneOf = (v: number) => (v > 0 ? 'is-up' : v < 0 ? 'is-down' : 'is-flat');
// Master name first; else the API name (it sends "Unknown" when it has none).
const subjectLabel = (s: AcademicExamCompareSubject) =>
  SUBJECT_NAMES[String(s.subject_id)] ??
  (s.subject_name && s.subject_name !== 'Unknown'
    ? s.subject_name
    : `Subject ${s.subject_id}`);
const EXAM_COLORS = {
  a: '#2f6eb5',
  b: '#9ccc50',
  diff: '#f0803c',
  pass: '#4a9e5c',
  board: '#7b1e3a',
};

type ExamPick = { cls: number; exam: number };
const TREND_COLORS = [
  '#e4842c',
  '#e4572e',
  '#f2c14e',
  '#7b1e3a',
  '#17a2b8',
  '#5cb85c',
  '#6f42c1',
  '#e8a33d',
  '#8b5e3c',
  '#2f5fb0',
  '#7cb342',
  '#8b1e3f',
];

// "SHOW N" selector for the trend line chart — how many entities (districts /
// blocks / schools) to plot. 38 = every district.
const TREND_SHOW_OPTIONS = [
  { label: '10', value: 10 },
  { label: '12', value: 12 },
  { label: '15', value: 15 },
  { label: '20', value: 20 },
  { label: '38', value: 38 },
];

const AcademicScores: React.FC<AcademicScoresProps> = ({
  year = '2026-27',
  activeSubTab = 'numbers',
  acdYr = null,
  districtId = null,
  blockId = null,
  schoolId = null,
  district = null,
  block = null,
  school = null,
  filters = EMPTY_FILTERS,
  onDistrictChange,
  onBlockChange,
  onSchoolChange,
  // Used ONLY by the Caution tab's flagged-school row click, which jumps
  // straight to a school and needs District + Block set together (the row
  // already carries its own district/block names) so the header dropdowns
  // sync and the drill breadcrumb — which only renders once a district is
  // set — appears above the grid.
  onGeoSelect,
}) => {
  const [band, setBand] = useState<ClassBand>('6-10');
  // Clicking a school row SELECTS that school (sets the header), which narrows
  // this whole tab — its charts + grid — to that one school. No detail card is
  // opened. Strips any trailing " (udise)" so the dropdown value matches.
  const selectSchool = (name: string) => {
    const bare = name.replace(/\s*\(\d[\d/]*\)\s*$/, '').trim();
    if (bare) onSchoolChange?.(bare);
  };

  // Current geography scope for every section / chart / table heading on this
  // tab, so a title never claims "Tamil Nadu" while the figures underneath it
  // are for one district, block or school.
  const pageScopeName = scopeLabel({ district, block, school });

  // ── Numbers tab: live academic APIs scoped by the header geography. The
  // "State Avg" radar polygon uses the same endpoint without geography.
  // Only set keys: RTK Query keys its cache on the args object, so null keys
  // would make the state-level "selected" call differ from the state-baseline
  // call ({ acd_yr }) and fire the same request twice.
  const numbersGeo = useMemo(
    () => ({
      ...(districtId != null ? { district_id: Number(districtId) } : {}),
      ...(blockId != null ? { block_id: Number(blockId) } : {}),
      ...(schoolId != null ? { school_id: Number(schoolId) } : {}),
    }),
    [districtId, blockId, schoolId]
  );
  // A school picked in the header (or from a schools list) narrows every
  // school-level grid to just that school — matched by school_id when the
  // row carries one, else by name.
  //
  // Match by id OR name, not id-only-when-present. The header's "Select
  // School" dropdown resolves schoolId from the schoollist API, while a
  // grid's rows (e.g. academicsubjectcomparison) carry their own school_id
  // from a different endpoint — those two ids are not guaranteed to share
  // the same namespace. Treating a present id as authoritative and skipping
  // the name check meant a namespace mismatch silently matched ZERO rows
  // (every row failed the id compare) even though the names were identical,
  // which is why picking a school from the header dropdown showed an empty/
  // fallback result while picking the exact same school by clicking a grid
  // row — whose name came from that grid's own data — always worked.
  const onlySelectedSchool = useCallback(
    <T,>(rows: T[], key: (r: T) => { id?: unknown; name?: unknown }): T[] => {
      if (!school) return rows;
      const want = school.trim().toLowerCase();
      return rows.filter((r) => {
        const { id, name } = key(r);
        const idMatches =
          schoolId != null &&
          id != null &&
          id !== '' &&
          String(id) === String(schoolId);
        const nameMatches =
          String(name ?? '')
            .trim()
            .toLowerCase() === want;
        return idMatches || nameMatches;
      });
    },
    [school, schoolId]
  );
  const numbersYr = acdYr != null ? Number(acdYr) : 0;
  const skip610 =
    acdYr == null || activeSubTab !== 'numbers' || band !== '6-10';
  const skip1112 =
    acdYr == null || activeSubTab !== 'numbers' || band !== '11-12';
  const sumSel610 = useGetAcademicSummaryQuery(
    { acd_yr: numbersYr, ...numbersGeo },
    { skip: skip610 }
  );
  const sumState610 = useGetAcademicSummaryQuery(
    { acd_yr: numbersYr },
    { skip: skip610 }
  );
  const sumSel1112 = useGetAcademicSummaryStreamQuery(
    { acd_yr: numbersYr, ...numbersGeo },
    { skip: skip1112 }
  );
  const sumState1112 = useGetAcademicSummaryStreamQuery(
    { acd_yr: numbersYr },
    { skip: skip1112 }
  );
  const examClass1112 = useGetAcademicExamClasswiseQuery(
    { acd_yr: numbersYr, ...numbersGeo },
    { skip: skip1112 }
  );
  const streams1112 = useGetAcademicStreamComparisonQuery(
    { acd_yr: numbersYr, ...numbersGeo },
    { skip: skip1112 }
  );
  const districts610 = useGetAcademicDistrictsQuery(
    { acd_yr: numbersYr, ...numbersGeo },
    { skip: skip610 }
  );
  const districts1112 = useGetAcademicDistrictsStreamQuery(
    { acd_yr: numbersYr, ...numbersGeo },
    { skip: skip1112 }
  );
  const numbersLoading =
    band === '6-10' ? sumSel610.isFetching : sumSel1112.isFetching;
  const apiData = useMemo(() => {
    if (band === '6-10') {
      const sel = withMarkedSubjects(sumSel610.data?.result);
      return buildAcademicFromApi(
        band,
        sel,
        withMarkedSubjects(sumState610.data?.result),
        sel?.exam_progression,
        sel?.class_wise,
        undefined
      );
    }
    const ec = examClass1112.data?.result;
    return buildAcademicFromApi(
      band,
      withMarkedSubjects(sumSel1112.data?.result),
      withMarkedSubjects(sumState1112.data?.result),
      ec?.exam_progression,
      ec?.class_wise,
      streams1112.data?.result?.stream_comparison
    );
  }, [
    band,
    sumSel610.data,
    sumState610.data,
    sumSel1112.data,
    sumState1112.data,
    examClass1112.data,
    streams1112.data,
  ]);
  // Drill-down table: live rows + the level the API broke down to.
  const districtsResp = (band === '6-10' ? districts610 : districts1112).data
    ?.result;
  const data = apiData ?? getAcademicData(band);

  // Sub-tab local controls
  const [trendMode, setTrendMode] = useState<'improved' | 'decline'>(
    'improved'
  );
  // How many district/block/school lines to show in the trend chart.
  const [trendShow, setTrendShow] = useState(12);
  const [trendMetric, setTrendMetric] = useState('avg');
  const [trendFrom, setTrendFrom] = useState<number | null>(null);
  const [trendTo, setTrendTo] = useState<number | null>(null);
  const [trendExam, setTrendExam] = useState<number | null>(null);
  const [trendClass, setTrendClass] = useState<number | null>(null);

  // ── Trend tab (academictrendcombined) ──
  const trendSkip = activeSubTab !== 'trend';
  // from_year / to_year are deliberately NOT part of the request. The API
  // already returns EVERY academic year in `by_year` and each row's
  // `yearly[]`, so the FROM/TO dropdowns are a client-side window applied in
  // renderTrend — changing them must not trigger a refetch. exam_type_id and
  // class_id DO change the aggregation, so they stay in the request key.
  const trendArgs = {
    district_id: districtId != null ? Number(districtId) : null,
    block_id: blockId != null ? Number(blockId) : null,
    school_id: schoolId != null ? Number(schoolId) : null,
    exam_type_id: trendExam,
    class_id: trendClass,
  };
  // ONE request for the whole tab. This used to be academictrend +
  // academictrenddistrict, which between them scanned academic_scores_abstract
  // three times over the same rows (including a year+exam-type grouping this
  // tab never rendered). The combined endpoint scans once at entity x year and
  // derives the state-level series from the same sums.
  const { data: trendCombinedResp, isFetching: trendLoading } =
    useGetAcademicTrendCombinedQuery(trendArgs, { skip: trendSkip });
  // Kept under the original names so the render code below is unchanged: both
  // sections now come out of the single response.
  const trendResp = trendCombinedResp;
  const trendDistResp = trendCombinedResp;
  void trendLoading;
  // ac_year id → label ("2025-26") from the commonmasters academic_year list.
  const { data: commonMasters } = useGetCommonMastersQuery(undefined, {
    skip: trendSkip,
  });
  const yearLabelById = useMemo(() => {
    const m = new Map<number, string>();
    (commonMasters?.result?.academic_year ?? []).forEach((y) =>
      m.set(Number(y.id), String(y.name))
    );
    return m;
  }, [commonMasters]);

  // Trend year window, derived entirely from the commonmasters academic_year
  // list + the header's SELECTED academic year (`acdYr`) — nothing hardcoded:
  //  • upper bound (TO) = the current academic year, i.e. the year chosen in
  //    the header (acdYr). If that is missing we fall back to the API-flagged
  //    in-progress/active year, else the latest year the API returned.
  //  • lower bound (FROM) = two academic years back from that current year, so
  //    a current year of 2026-2027 yields 2024-2025 … 2026-2027.
  // Future years and anything older than the window are dropped. The current
  // year is always included even if the API list hasn't got it yet.
  const TREND_WINDOW_YEARS = 2; // current year + 2 earlier = 3 selectable years
  const trendYearOptions = useMemo(() => {
    const rows = commonMasters?.result?.academic_year ?? [];
    const startYearOf = (name: string): number => {
      const match = String(name).match(/(\d{4})/);
      return match ? Number(match[1]) : NaN;
    };
    const isCurrentFlag = (y: {
      status?: string | number;
      isactive?: string | number;
    }): boolean => {
      const st = String(y.status ?? '')
        .toLowerCase()
        .replace(/[^a-z]/g, '');
      const act = String(y.isactive ?? '').toLowerCase();
      return (
        st === 'inprogress' ||
        st === 'current' ||
        st === 'active' ||
        act === '1' ||
        act === 'yes' ||
        act === 'true'
      );
    };

    const withStart = rows
      .map((y) => ({ ...y, _start: startYearOf(y.name) }))
      .filter((y) => !Number.isNaN(y._start));
    if (withStart.length === 0) return [];

    // Current academic year's start. Prefer the header's selected year
    // (acdYr → its label → start), then the API-flagged current year, then the
    // latest year present.
    const selectedRow =
      acdYr != null
        ? withStart.find((y) => Number(y.id) === Number(acdYr))
        : undefined;
    const flaggedRow = withStart.find((y) => isCurrentFlag(y));
    const currentStart = selectedRow
      ? selectedRow._start
      : flaggedRow
        ? flaggedRow._start
        : Math.max(...withStart.map((y) => y._start));
    const floorStart = currentStart - TREND_WINDOW_YEARS;

    // Short label "2024-25" from a "2024-2025" / "2024-25" API name.
    const shortLabel = (name: string, start: number): string => {
      const match = String(name).match(/(\d{4})\s*[-/]\s*(\d{2,4})/);
      const end = match ? Number(match[2].slice(-2)) : (start + 1) % 100;
      return `${start}-${String(end).padStart(2, '0')}`;
    };

    const seen = new Set<number>();
    return withStart
      .filter((y) => y._start >= floorStart && y._start <= currentStart)
      .sort((a, b) => a._start - b._start)
      .filter((y) => {
        if (seen.has(y._start)) return false;
        seen.add(y._start);
        return true;
      })
      .map((y) => ({
        label: shortLabel(y.name, y._start),
        value: Number(y.id),
      }));
  }, [commonMasters, acdYr]);

  const [comparisonMode, setComparisonMode] = useState<'districts' | 'exams'>(
    'districts'
  );
  const [comparisonMetric, setComparisonMetric] = useState('avg');
  const [alarmFilter, setAlarmFilter] = useState<'all' | 'compliance'>('all');
  const [cautionCheck, setCautionCheck] = useState<string>(
    'zero_mark_entry_compliance'
  );

  // ── Caution tab (academic compliance cards + list) ──
  const geoScope = useMemo(
    () => ({
      ...(districtId != null ? { district_id: Number(districtId) } : {}),
      ...(blockId != null ? { block_id: Number(blockId) } : {}),
      ...(schoolId != null ? { school_id: Number(schoolId) } : {}),
    }),
    [districtId, blockId, schoolId]
  );
  const cautionAcYr = acdYr != null ? Number(acdYr) : undefined;
  const activeCautionCard =
    ACADEMIC_CAUTION_CARDS.find((c) => c.key === cautionCheck) ??
    ACADEMIC_CAUTION_CARDS[0];
  const { data: compCardsResp, isFetching: compCardsLoading } =
    useGetAcademicComplianceCardsQuery(
      { acd_yr: cautionAcYr, ...geoScope },
      { skip: cautionAcYr == null || activeSubTab !== 'caution' }
    );
  // All four caution bands in ONE request. This used to refetch the list on
  // every tile click (one call per band); now the bands are fetched together
  // from a single backend scan and switching tiles is instant with no network.
  const { data: compListAllResp, isFetching: compListLoading } =
    useGetAcademicComplianceListMultiQuery(
      {
        acd_yr: cautionAcYr,
        flags: ACADEMIC_CAUTION_FLAGS,
        ...geoScope,
      },
      { skip: cautionAcYr == null || activeSubTab !== 'caution' }
    );
  // The selected tile's rows, sliced out of that response. Shaped to match the
  // old single-band response so the render code below is unchanged.
  const compListResp = useMemo(
    () => ({
      result:
        compListAllResp?.result?.by_flag?.[String(activeCautionCard.flag)] ??
        [],
    }),
    [compListAllResp, activeCautionCard.flag]
  );
  const { data: allDistrictsForCaution } = useGetAllDistrictListQuery();
  const districtIdToNameCaution = useMemo(() => {
    const m = new Map<string, string>();
    (allDistrictsForCaution?.result?.schooldist ?? []).forEach((d) => {
      if (d?.id != null && d.district_name)
        m.set(String(d.id), String(d.district_name));
    });
    return m;
  }, [allDistrictsForCaution]);

  // ── Comparison tab: Between Districts (academicsubjectcomparison) ──
  const subjectCmpMetric =
    comparisonMetric === 'pass' ? 2 : comparisonMetric === 'compliance' ? 3 : 1;
  // This endpoint does not support a school_id scope — given one, it ignores
  // district_id/block_id too and falls back to the full, unscoped state-wide
  // district list (the bug: picking a school in the header made the heatmap
  // jump back to "Perambalur, Thanjavur, Tirunelveli…" instead of showing
  // just that school). Scope the request by district/block only, the same
  // request that correctly returns the block's schools; narrowing to the one
  // selected school happens client-side via onlySelectedSchool below, same
  // as every other grid in this file.
  const subjectCmpGeoScope = useMemo(
    () => ({
      ...(districtId != null ? { district_id: Number(districtId) } : {}),
      ...(blockId != null ? { block_id: Number(blockId) } : {}),
    }),
    [districtId, blockId]
  );
  const { data: subjectCmpResp, isFetching: subjectCmpLoading } =
    useGetAcademicSubjectComparisonQuery(
      { acd_yr: cautionAcYr, metric: subjectCmpMetric, ...subjectCmpGeoScope },
      {
        skip:
          cautionAcYr == null ||
          activeSubTab !== 'comparison' ||
          comparisonMode !== 'districts',
      }
    );
  const subjectCmp = subjectCmpResp?.result;

  // ── Comparison tab: ranked bar chart source (academicdistricts) ──
  // The ranked chart below is driven by this endpoint — one row per entity
  // with avg_mark / pass_pct / compliance — NOT by the heatmap's subject
  // comparison. band 6-10 → academicdistricts, 11-12 → ...stream. Clicking a
  // bar drills (district → block → school), so the query re-scopes with the
  // header and returns the next level's rows.
  const rankedResp610 = useGetAcademicDistrictsQuery(
    { acd_yr: cautionAcYr ?? 0, ...geoScope },
    {
      skip:
        cautionAcYr == null ||
        activeSubTab !== 'comparison' ||
        comparisonMode !== 'districts' ||
        band !== '6-10',
    }
  );
  const rankedResp1112 = useGetAcademicDistrictsStreamQuery(
    { acd_yr: cautionAcYr ?? 0, ...geoScope },
    {
      skip:
        cautionAcYr == null ||
        activeSubTab !== 'comparison' ||
        comparisonMode !== 'districts' ||
        band !== '11-12',
    }
  );
  const rankedActive = band === '6-10' ? rankedResp610 : rankedResp1112;
  const rankedResp = rankedActive.data?.result;
  const rankedLoading = rankedActive.isFetching;

  const subjectCmpColumns = useMemo(
    () => (subjectCmp?.subjects ?? []).map((sub) => sub.subject_name),
    [subjectCmp]
  );
  // Normalised entity rows for both the heatmap AND the ranked bar chart.
  // Each row carries its label, per-subject values, an `overall` metric used
  // for ranking/colouring, and the entity name for drill-down.
  //
  // Level resolution: match by prefix (startsWith), not exact string, since
  // other endpoints in this app return either 'school'/'block'/'district'
  // (singular) or 'schools'/'blocks'/'districts' (plural). If the response
  // carries no usable level at all, INFER it from the request scope that was
  // actually sent — schoolId → schools, blockId (no school) → blocks, else →
  // districts. This was previously a hard school/not-school split, which
  // treated BLOCK rows as districts: every block in the selected district got
  // looked up by districtIdToNameCaution (keyed on district_id), so every row
  // showed the SAME district name instead of its own block name — the
  // heatmap looked frozen/"disabled" the moment a block was selected.
  const subjectCmpLevelStr = String(subjectCmp?.level ?? '').toLowerCase();
  const subjectCmpInferredLevel: 'schools' | 'blocks' | 'districts' =
    schoolId != null ? 'schools' : blockId != null ? 'blocks' : 'districts';
  const subjectCmpLevel = subjectCmpLevelStr.startsWith('school')
    ? 'schools'
    : subjectCmpLevelStr.startsWith('block')
      ? 'blocks'
      : subjectCmpLevelStr.startsWith('district')
        ? 'districts'
        : subjectCmpInferredLevel; // no usable level string → infer from scope
  const subjectCmpIsSchool = subjectCmpLevel === 'schools';
  const subjectCmpIsBlock = subjectCmpLevel === 'blocks';
  const subjectCmpLevelNoun = subjectCmpIsSchool
    ? 'School'
    : subjectCmpIsBlock
      ? 'Block'
      : 'District';
  // Clicking a heatmap row drills one level deeper — same district → block →
  // school convention used by every other grid/chart in this file (e.g.
  // rankedDrill below). Schools are the leaf, so a click there just (re)
  // selects that school instead of drilling further.
  const subjectCmpDrill = (name: string) => {
    if (!name) return;
    if (subjectCmpIsSchool) selectSchool(name);
    else if (subjectCmpIsBlock) onBlockChange?.(name);
    else onDistrictChange?.(name);
  };
  const subjectCmpEntities = useMemo(() => {
    const ids = subjectCmp?.subject_ids ?? [];
    const rows = subjectCmp?.rows ?? [];
    return (
      subjectCmpIsSchool
        ? onlySelectedSchool(rows, (r) => ({
            id: r.school_id,
            name: r.name ?? r.school_name,
          }))
        : rows
    ).map((r) => {
      const values = ids.map((id) => Number(r.subjects?.[id]) || 0);
      // Prefer the API's `overall`; otherwise average the subject values so
      // the chart still ranks sensibly.
      const overall =
        r.overall != null && !Number.isNaN(Number(r.overall))
          ? Number(r.overall)
          : values.length
            ? values.reduce((t, v) => t + v, 0) / values.length
            : 0;
      // Multiple possible name keys, same ?? chain convention used elsewhere
      // in this app (e.g. Attendance.tsx's toSummaryRow) — the exact field
      // name an endpoint uses for a row's own name varies by level.
      const label = subjectCmpIsSchool
        ? String(r.name ?? r.school_name ?? '')
        : subjectCmpIsBlock
          ? String(r.name ?? r.block_name ?? '')
          : (districtIdToNameCaution.get(String(r.district_id ?? r.name)) ??
            String(r.district_name ?? r.name ?? ''));
      return { label, values, overall: Math.round(overall * 10) / 10 };
    });
  }, [
    subjectCmp,
    subjectCmpIsSchool,
    subjectCmpIsBlock,
    districtIdToNameCaution,
    onlySelectedSchool,
  ]);

  // Heatmap rows (label + per-subject values). Sorted weakest-overall first so
  // the problem entities surface at the TOP; the API returned them in an
  // arbitrary order, which is the "wrong order" the heatmap showed. Keeps the
  // heatmap and the ranked chart below telling the same story.
  const subjectCmpRows = useMemo(
    () =>
      [...subjectCmpEntities]
        .sort((a, b) => a.overall - b.overall)
        .map((e) => ({ label: e.label, values: e.values })),
    [subjectCmpEntities]
  );

  // Colour-scale domain derived from the ACTUAL values, not a hardcoded 0–100.
  //
  // The subject-comparison API returns different scales per metric (marks ~44–83,
  // pass ~90–99, compliance ~0.5–2.7). Pinning 0–100 made the small-valued
  // metrics collapse to the far-left orange end — every cell orange, and the
  // 0–100 hover range meaningless. Spanning the data's own min→max means the
  // weakest cell is orange and the strongest is green on every metric, and the
  // legend reads against the real range.
  const heatmapDomain = useMemo(() => {
    const all = subjectCmpRows
      .flatMap((r) => r.values)
      .filter((v) => Number.isFinite(v));
    if (!all.length) {
      return {
        min: 0,
        max: 100,
        suffix: comparisonMetric === 'pass' ? '%' : '',
      };
    }
    const lo = Math.min(...all);
    const hi = Math.max(...all);
    // A touch of head/foot room so the extreme cells aren't the pure endpoint
    // colours, and a guard for the degenerate all-equal case.
    const pad = hi > lo ? (hi - lo) * 0.08 : Math.max(1, hi * 0.1);
    return {
      min: Math.max(0, Math.floor((lo - pad) * 10) / 10),
      max: Math.ceil((hi + pad) * 10) / 10,
      suffix: comparisonMetric === 'pass' ? '%' : '',
    };
  }, [comparisonMetric, subjectCmpRows]);

  // ── Ranked bar chart (academicdistricts, driven by the METRIC dropdown) ──
  // The value shown/ranked follows the dropdown: Average Mark → avg_mark,
  // Pass % → pass_pct, Compliance → compliance.
  const rankedMetricKey: 'avg_mark' | 'pass_pct' | 'compliance' =
    comparisonMetric === 'pass'
      ? 'pass_pct'
      : comparisonMetric === 'compliance'
        ? 'compliance'
        : 'avg_mark';
  const rankedMetricLabel =
    comparisonMetric === 'pass'
      ? 'Pass %'
      : comparisonMetric === 'compliance'
        ? 'Compliance'
        : 'Average Mark';
  // avg_mark is a raw score; pass_pct / compliance are percentages.
  const rankedSuffix = comparisonMetric === 'avg' ? '' : '%';

  // The level academicdistricts broke down to drives drill + the bar label.
  const rankedLevel = String(rankedResp?.level ?? 'districts');
  const rankedCanDrill = !rankedLevel.startsWith('school');
  const rankedLevelNoun = rankedLevel.startsWith('school')
    ? 'School'
    : rankedLevel.startsWith('block')
      ? 'Block'
      : 'District';

  // One entity per row, ranked best → worst by the selected metric. When a
  // school is selected in the header, narrow to just that one school so the
  // ranked bar shows a single bar in sync with the selection.
  const rankedEntities = useMemo(() => {
    const srcRows = rankedResp?.rows ?? [];
    const scopedRows = rankedLevel.startsWith('school')
      ? onlySelectedSchool(srcRows, (r) => ({ id: r.school_id, name: r.name }))
      : srcRows;
    return scopedRows
      .map((r) => ({
        name: String(r.name ?? ''),
        value: Math.round((Number(r[rankedMetricKey]) || 0) * 10) / 10,
      }))
      .filter((e) => e.name)
      .sort((a, b) => b.value - a.value);
  }, [rankedResp, rankedMetricKey, rankedLevel, onlySelectedSchool]);

  const rankedAverage = useMemo(() => {
    if (!rankedEntities.length) return 0;
    const sum = rankedEntities.reduce((t, e) => t + e.value, 0);
    return Math.round((sum / rankedEntities.length) * 10) / 10;
  }, [rankedEntities]);

  const rankedLabels = rankedEntities.map((e) => e.name);
  const rankedSeries: ChartSeries[] = [
    {
      label: rankedMetricLabel,
      data: rankedEntities.map((e) => e.value),
      color: '#2e7d32',
      // At/above the average is green, below is red — the convention the
      // Attendance comparison chart uses.
      barColors: rankedEntities.map((e) =>
        e.value >= rankedAverage ? '#2e7d32' : '#c0392b'
      ),
    },
  ];
  const rankedDrill = (name: string) => {
    if (!name || !rankedCanDrill) return;
    if (rankedLevel.startsWith('block')) onBlockChange?.(name);
    else onDistrictChange?.(name);
  };

  void subjectCmpLoading;
  void numbersLoading;

  // ── Comparison tab: Between Exams ──
  const [examA, setExamA] = useState<ExamPick>({ cls: 6, exam: 1 });
  const [examB, setExamB] = useState<ExamPick>({ cls: 6, exam: 3 });
  const [examSubject, setExamSubject] = useState<number | null>(null);
  const [sittingsClass, setSittingsClass] = useState<number | null>(null);
  const examsSkip =
    cautionAcYr == null ||
    activeSubTab !== 'comparison' ||
    comparisonMode !== 'exams';
  const examAcYr = cautionAcYr ?? 0;
  // Header geography (district / block / school) scopes every exam API.
  const examGeo = {
    district_id: districtId != null ? Number(districtId) : null,
    block_id: blockId != null ? Number(blockId) : null,
    school_id: schoolId != null ? Number(schoolId) : null,
  };
  const comparePair = {
    acd_yr: examAcYr,
    class_a: examA.cls,
    exam_a: examA.exam,
    class_b: examB.cls,
    exam_b: examB.exam,
    ...examGeo,
  };
  // A vs B, scoped to the optional subject.
  const { data: examCmpResp, isFetching: examCmpLoading } =
    useGetAcademicExamCompareQuery(
      { ...comparePair, subject_id: examSubject },
      { skip: examsSkip }
    );
  // Unfiltered A vs B — feeds the subject dropdown, the subject chart and
  // table (same request as above when no subject is picked, so de-duplicated).
  const { data: examCmpAllResp, isFetching: examCmpAllLoading } =
    useGetAcademicExamCompareQuery(
      { ...comparePair, subject_id: null },
      { skip: examsSkip }
    );
  // Exam vs Subject for Exam A's class: Quarterly → Half Yearly and
  // Quarterly → Annual give each subject's mark in all three exams.
  const classPair = {
    acd_yr: examAcYr,
    class_a: examA.cls,
    exam_a: 1,
    class_b: examA.cls,
    subject_id: null,
    ...examGeo,
  };
  const { data: examQhyResp, isFetching: examQhyLoading } =
    useGetAcademicExamCompareQuery(
      { ...classPair, exam_b: 2 },
      { skip: examsSkip }
    );
  const { data: examQaResp, isFetching: examQaLoading } =
    useGetAcademicExamCompareQuery(
      { ...classPair, exam_b: 3 },
      { skip: examsSkip }
    );
  const { data: sittingsResp, isFetching: sittingsLoading } =
    useGetAcademicExamSittingsQuery(
      {
        acd_yr: examAcYr,
        class_id: null,
        exam_type_id: null,
        ...examGeo,
      },
      { skip: examsSkip }
    );
  const { data: improvementResp, isFetching: improvementLoading } =
    useGetAcademicExamDistrictImprovementQuery(
      {
        acd_yr: examAcYr,
        exam_a: examA.exam,
        exam_b: examB.exam,
        class_from: Math.min(examA.cls, examB.cls),
        class_to: Math.max(examA.cls, examB.cls),
        ...examGeo,
      },
      { skip: examsSkip }
    );

  // ─────────────────────────────────────────────
  // Shared bits
  // ─────────────────────────────────────────────
  const scopeNote = (
    <p className="academic-scores__scope">
      <i className="pi pi-info-circle" />
      <span>
        <strong>Scope:</strong> {ACADEMIC_SCOPE_NOTE}
      </span>
    </p>
  );

  const yearChip = (
    <div className="academic-scores__year">
      <span className="academic-scores__year-label">ACADEMIC YEAR</span>
      <span className="academic-scores__year-value">{year}</span>
    </div>
  );

  // ═════════════════════════════════════════════
  // NUMBERS
  // ═════════════════════════════════════════════
  const renderNumbers = () => {
    const profileSeries: ChartSeries[] = [
      {
        label: `${pageScopeName}`,
        data: data.subjectProfile.selected,
        color: '#7e57c2',
      },
      {
        label: 'Tamil Nadu average',
        data: data.subjectProfile.state,
        color: '#2f5fb0',
      },
    ];
    const subjectWiseAvgandPAss: ChartSeries[] = [
      {
        label: 'Pass %',
        data: data.markBand.passPct,
        color: '#4a9e5c',
        asLine: true,
      },
      { label: 'Average Mark', data: data.markBand.avgMark, color: '#2f5fb0' },
    ];
    const markBandSeries: ChartSeries[] = [
      {
        label: 'Below 35',
        data: data.markBand.below35,
        tableData: data.markBand.below35Raw,
        color: MARK_BAND_COLORS.below35,
      },
      {
        label: '35 - 60',
        data: data.markBand.band35_60,
        tableData: data.markBand.band35_60Raw,
        color: MARK_BAND_COLORS.band35_60,
      },
      {
        label: '61 - 80',
        data: data.markBand.band61_80,
        tableData: data.markBand.band61_80Raw,
        color: MARK_BAND_COLORS.band61_80,
      },
      {
        label: 'Above 80',
        data: data.markBand.above80,
        tableData: data.markBand.above80Raw,
        color: MARK_BAND_COLORS.above80,
      },
    ];

    // Create custom tooltip details with actual numbers (not in lakhs)
    const markBandTooltipDetails: Record<
      string,
      { label: string; value: string }[]
    > = {};
    data.markBand.subjects.forEach((subject, index) => {
      const originalData =
        data.originalSubjectDetail?.[index] || data.subjectDetail[index];
      markBandTooltipDetails[subject] = [
        {
          label: 'Below 35',
          value: fmtIndian(originalData?.below35 || 0),
        },
        {
          label: '35 - 60',
          value: fmtIndian(originalData?.band3560 || 0),
        },
        {
          label: '61 - 80',
          value: fmtIndian(originalData?.band6180 || 0),
        },
        {
          label: 'Above 80',
          value: fmtIndian(originalData?.above80 || 0),
        },
      ];
    });

    const examProgressionSeries: ChartSeries[] = [
      {
        label: 'Pass %',
        data: data.examProgression.passPct,
        color: '#4a9e5c',
        asLine: true,
        showPointLabels: true,
        pointLabelSuffix: '%',
      },
      {
        label: 'Compliance %',
        data: data.examProgression.compliance,
        color: '#f0803c',
        asLine: true,
      },
      {
        label: 'Average Mark',
        data: data.examProgression.avgMark,
        color: '#2f5fb0',
      },
    ];

    const classWiseSeries: ChartSeries[] = [
      {
        label: 'Pass %',
        data: data.classWise.passPct,
        color: '#4a9e5c',
        asLine: true,
      },
      { label: 'Average Mark', data: data.classWise.avgMark, color: '#2f5fb0' },
    ];

    const subjectColumns: ReportColumn[] = [
      { key: 'subject', label: 'Subject', type: 'text', bold: true },
      {
        key: 'averageMark',
        label: 'Average Mark',
        type: 'threshold',
        // Green when the average mark is above 60; neutral otherwise (bad: 0
        // keeps lower marks plain rather than red).
        thresholds: { good: 60, bad: 0 },
      },
      { key: 'passPct', label: 'Pass %', type: 'text' },
      {
        key: 'below35',
        label: 'Below 35',
        type: 'number',
        textColor: '#dc2626', // always red
      },
      { key: 'band3560', label: '35-60', type: 'number' },
      { key: 'band6180', label: '61-80', type: 'number' },
      {
        key: 'above80',
        label: 'Above 80',
        type: 'number',
        textColor: '#16a34a', // always green
      },
      { key: 'absentees', label: 'Absentees', type: 'number' },
    ];

    // Create table data with original unscaled numbers for display
    const subjectTableData = (
      data.originalSubjectDetail || data.subjectDetail
    ).map((item) => ({
      ...item,
      // Convert back to original numbers if they were scaled to lakhs
      // below35: data.markBandInLakh
      //   ? Math.round(item.below35)
      //   : item.below35,
      // band3560: data.markBandInLakh
      //   ? Math.round(item.band3560)
      //   : item.band3560,
      // band6180: data.markBandInLakh
      //   ? Math.round(item.band6180)
      //   : item.band6180,
      // above80: data.markBandInLakh
      //   ? Math.round(item.above80)
      //   : item.above80,
      below35: item.below35 ? Math.round(item.below35) : item.below35,
      band3560: item.band3560 ? Math.round(item.band3560) : item.band3560,
      band6180: item.band6180 ? Math.round(item.band6180) : item.band6180,
      above80: item.above80 ? Math.round(item.above80) : item.above80,
    }));
    console.log(data.originalSubjectDetail, 'data.originalSubjectDetail');
    console.log(subjectTableData, 'subjectTableData');
    //console.log('Debug - Table data:', {
    //   originalSubjectDetail: data.originalSubjectDetail?.[0],
    //   subjectDetail: data.subjectDetail[0],
    //   subjectTableData: subjectTableData[0],
    //   markBandInLakh: data.markBandInLakh,
    // });

    const summaryFields: DrillField[] = [
      { key: 'name', type: 'name' },
      { key: 'avgMark', type: 'avg' },
      { key: 'passPct', type: 'pct' },
      { key: 'compliance', type: 'pct' },
      { key: 'weakestSubject', type: 'text' },
    ];
    // Live drill rows (academicdistricts[stream]); mock until they arrive.
    const drill = districtsResp
      ? {
          level:
            districtsResp.level === 'schools'
              ? 2
              : districtsResp.level === 'blocks'
                ? 1
                : 0,
          rows: (districtsResp.level === 'schools'
            ? onlySelectedSchool(districtsResp.rows ?? [], (r) => ({
                id: r.school_id,
                name: r.name,
              }))
            : (districtsResp.rows ?? [])
          ).map(toSummaryRow(band)),
        }
      : buildDrilldown({
          baseRows: districtAcademicSummary,
          fields: summaryFields,
          district,
          block,
          school,
          filters,
        });
    const nameLabel = drillLevelLabel(drill.level);
    const summaryTitle =
      drill.level === 3
        ? `School — ${school}`
        : drill.level === 2
          ? `Schools — ${block}`
          : drill.level === 1
            ? `Blocks — ${district}`
            : 'Districts — Academic Summary';

    const districtColumns: ReportColumn[] = [
      {
        // 'link' at every level: district/block rows drill deeper, a school
        // row (the leaf) opens its School Detail view.
        key: 'name',
        label: nameLabel,
        type: 'link',
      },
      {
        key: 'avgMark',
        label: 'Avg Mark',
        type: 'threshold',
        // Green when avg mark is above 60; neutral below (bad: 0 → not red).
        thresholds: { good: 60, bad: 0 },
      },
      { key: 'passPct', label: 'Pass %', type: 'text' },
      {
        key: 'compliance',
        label: 'Compliance',
        type: 'threshold',
        // Red when compliance is below 85%; neutral at/above (good: 100 keeps
        // normal values plain, only < 85 turns red).
        thresholds: { good: 100, bad: 85 },
      },
      { key: 'weakestSubject', label: 'Weakest Subject', type: 'text' },
    ];

    const handleSummaryRowClick = (row: Record<string, unknown>) => {
      const name = String(row.name ?? '');
      if (drill.level === 0) onDistrictChange?.(name);
      else if (drill.level === 1) onBlockChange?.(name);
      else if (drill.level === 2) selectSchool(name); // leaf → select the school
    };

    return (
      <>
        <SectionTitle
          icon="pi pi-graduation-cap"
          text={`Academic Scores — ${pageScopeName}`}
          year={year}
        />

        <div className="row g-3 mb-3">
          <div className="col-12 col-md-4">
            <StatTile
              icon="pi pi-pencil"
              value={`${data.kpis.averageMark}`}
              label="Average Mark"
            />
          </div>
          <div className="col-12 col-md-4">
            <StatTile
              icon="pi pi-check-circle"
              value={`${data.kpis.passPercentage}%`}
              label="Pass Percentage"
            />
          </div>
          <div className="col-12 col-md-4">
            <StatTile
              icon="pi pi-verified"
              value={`${data.kpis.compliance}%`}
              label="Compliance"
            />
          </div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-6 col-lg">
            <MetricCard
              value={fmtIndian(data.metrics.studentsExpected)}
              label="Students Expected"
            />
          </div>
          <div className="col-6 col-lg">
            <MetricCard
              value={fmtIndian(data.metrics.marksEntered)}
              label="Marks Entered"
            />
          </div>
          <div className="col-6 col-lg">
            <MetricCard
              value={`${data.metrics.strongestSubject.name} (${data.metrics.strongestSubject.value})`}
              label="Strongest Subject"
              accent="#22c55e"
            />
          </div>
          <div className="col-6 col-lg">
            <MetricCard
              value={`${data.metrics.weakestSubject.name} (${data.metrics.weakestSubject.value})`}
              label="Weakest Subject"
              accent="#e4572e"
            />
          </div>
          <div className="col-6 col-lg">
            <MetricCard
              value={`${data.metrics.below35Pct}%`}
              label="Below 35 Marks"
              accent="#f0803c"
            />
          </div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-6">
            <AppChart
              type="radar"
              title="Subject Profile vs State"
              icon="pi pi-chart-line"
              iconColor="#2f5fb0"
              titleSuffix={
                data.subjectCount != null &&
                data.subjectProfile.labels.length < data.subjectCount
                  ? `top & bottom ${data.subjectProfile.labels.length} of ${data.subjectCount} subjects`
                  : data.profileBadge
              }
              labels={data.subjectProfile.labels}
              series={profileSeries}
              height={320}
              radarTooltipScope={pageScopeName}
            />
          </div>
          <div className="col-12 col-lg-6">
            <AppChart
              type="stackedBar"
              title={data.markBandTitle}
              icon="pi pi-chart-bar"
              iconColor="#2e7d32"
              labels={data.markBand.subjects}
              series={markBandSeries}
              yTitle="Students"
              yTickSuffix={data.markBandInLakh === false ? '' : ' L'}
              tooltipDetails={markBandTooltipDetails}
              height={320}
            />
          </div>
        </div>

        <div className="mb-4">
          <AppChart
            type="combo"
            title="Subject-wise Average Mark & Pass %"
            icon="pi pi-sitemap"
            iconColor="#7e57c2"
            // Subject-wise, not stream-wise: x-axis is the subjects and the
            // series are their Average Mark (bars) + Pass % (line).
            labels={data.markBand.subjects}
            series={subjectWiseAvgandPAss}
            yTitle="Marks / %"
            yMax={120}
            height={300}
          />
        </div>

        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-6">
            <AppChart
              type="combo"
              title="Exam Progression (Quarterly → Annual)"
              icon="pi pi-sliders-h"
              iconColor="#2f5fb0"
              labels={data.examProgression.periods}
              series={examProgressionSeries}
              yTitle="Marks / %"
              yTickSuffix=""
              yMax={120}
              legendPosition="top"
              height={320}
            />
          </div>
          <div className="col-12 col-lg-6">
            <AppChart
              type="combo"
              title="Class-wise Performance"
              titleSuffix="maroon = board exam classes"
              icon="pi pi-chart-bar"
              iconColor="#8b1e3f"
              labels={data.classWise.classes}
              series={classWiseSeries}
              yTitle="Marks / %"
              yTickSuffix=""
              yMax={120}
              legendPosition="top"
              xTitle="Class"
              highlightLabel={data.classWise.boardClasses[0]}
              height={320}
            />
          </div>
        </div>

        <div className="mb-4">
          <ReportDataTable
            key={`subjects-${band}`}
            title={data.subjectDetailTitle}
            hint={`${data.subjectDetail.length} subjects`}
            icon="pi pi-book"
            columns={subjectColumns}
            data={subjectTableData}
            searchable={false}
            exportFileName={`academic-subjects-${band}`}
            rows={15}
            rowsPerPageOptions={[15, 30]}
          />
        </div>

        <div className="mb-4">
          <DrillBreadcrumb
            district={district}
            block={block}
            school={school}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
            onSchoolChange={onSchoolChange}
          />
          <ReportDataTable
            key={`${district ?? 'all'}-${block ?? ''}-${school ?? ''}`}
            title={summaryTitle}
            hint={drillHint(drill.level)}
            countBadge={`${drill.rows.length} total`}
            icon="pi pi-table"
            // columns={districtColumns}
            columns={
              school && drill.rows.length === 1
                ? [
                    { ...districtColumns[0], type: 'text' },
                    ...districtColumns.slice(1),
                  ]
                : districtColumns
            }
            data={drill.rows}
            searchPlaceholder={drillSearchPlaceholder(drill.level)}
            exportFileName="academic-summary"
            rows={12}
            onRowClick={handleSummaryRowClick}
          />
        </div>
      </>
    );
  };

  // ═════════════════════════════════════════════
  // TREND ANALYSIS
  // ═════════════════════════════════════════════
  const renderTrend = () => {
    // ── Live data (academictrend / academictrenddistrict); mock fallback ──
    const byYear = trendResp?.result?.by_year ?? [];
    const entityRowsAll = trendDistResp?.result?.rows ?? [];
    const entityRows =
      trendDistResp?.result?.level === 'schools'
        ? onlySelectedSchool(entityRowsAll, (r) => ({
            id: r.school_id,
            name: r.name,
          }))
        : entityRowsAll;
    const yearLabel = (id: string | number) =>
      yearLabelById.get(Number(id)) ?? String(id);

    // Client-side FROM/TO window. The API returns every year in `by_year`;
    // the dropdowns just restrict which of them is plotted, so changing them
    // slices the already-fetched data instead of refetching. trendFrom/trendTo
    // are academic-year ids; keep only years whose id falls between them
    // (inclusive), ordered by the trendYearOptions list.
    //
    // Effective bounds mirror the dropdowns' own defaults (earliest selectable
    // → latest), so before the user picks anything the plotted window matches
    // what the FROM/TO boxes display.
    const yearOrder = trendYearOptions.map((o) => Number(o.value));
    const effFrom =
      trendFrom != null && yearOrder.includes(trendFrom)
        ? trendFrom
        : (yearOrder[0] ?? null);
    const effTo =
      trendTo != null && yearOrder.includes(trendTo)
        ? trendTo
        : (yearOrder[yearOrder.length - 1] ?? null);
    const inWindow = (acYearId: string | number): boolean => {
      const id = Number(acYearId);
      if (effFrom == null || effTo == null) return true;
      const pos = yearOrder.indexOf(id);
      // A year not in the option list (older/future) is outside the window.
      if (pos === -1) return false;
      const lo = Math.min(yearOrder.indexOf(effFrom), yearOrder.indexOf(effTo));
      const hi = Math.max(yearOrder.indexOf(effFrom), yearOrder.indexOf(effTo));
      return pos >= lo && pos <= hi;
    };
    const byYearWindowed = byYear.filter((p) => inWindow(p.ac_year));

    const live = byYearWindowed.length > 0;
    const yearIds = live ? byYearWindowed.map((p) => String(p.ac_year)) : [];
    const years = live ? yearIds.map(yearLabel) : TREND.years;
    const firstYear = years[0] ?? '';
    const lastYear = years[years.length - 1] ?? '';
    const metricLabel =
      metricOptions.find((m) => m.value === trendMetric)?.label ??
      'Average Mark';

    // Base view is a line chart; the Bar / Line toolbar icons switch the view
    // (AppChart strips `asLine` in Bar view automatically). Only the Average
    // Mark series shows its value above each point/bar; Pass % and Compliance %
    // stay unlabelled to keep the chart clean.
    const stateSeries: ChartSeries[] = [
      {
        label: 'Average Mark',
        data: live
          ? byYearWindowed.map((p) => n0(p.average_mark))
          : TREND.averageMark,
        color: '#2f5fb0',
        asLine: true,
        showPointLabels: true,
      },
      {
        label: 'Pass %',
        data: live ? byYearWindowed.map((p) => n0(p.pass_pct)) : TREND.passPct,
        color: '#5cb85c',
        asLine: true,
      },
      {
        label: 'Compliance %',
        data: live
          ? byYearWindowed.map((p) => n0(p.compliance))
          : TREND.compliance,
        color: '#f0803c',
        asLine: true,
      },
    ];

    // Per entity: one value per year (aligned to the state years) + change
    // between its first and last year with data, for the chosen metric.
    type EntityTrend = {
      name: string;
      values: number[];
      from: number;
      to: number;
      change: number;
    };
    const entities: EntityTrend[] = live
      ? entityRows.map((r) => {
          const byId = new Map(r.yearly.map((p) => [String(p.ac_year), p]));
          // Values aligned to the WINDOWED years only.
          const values = yearIds.map((y) =>
            trendValue(byId.get(y), trendMetric)
          );
          // Change measured across the window's first → last year (not the
          // row's full history), so the FROM/TO selection drives it.
          const from = yearIds.length
            ? trendValue(byId.get(yearIds[0]), trendMetric)
            : 0;
          const to = yearIds.length
            ? trendValue(byId.get(yearIds[yearIds.length - 1]), trendMetric)
            : 0;
          return { name: r.name, values, from, to, change: r1(to - from) };
        })
      : DISTRICT_TRENDS.map((d) => ({
          name: d.name,
          values: d.values,
          from: d.values[0],
          to: d.values[d.values.length - 1],
          change: r1(d.values[d.values.length - 1] - d.values[0]),
        }));
    const sorted = [...entities].sort((a, b) =>
      trendMode === 'improved' ? b.change - a.change : a.change - b.change
    );

    // Show the first `trendShow` lines (user-selectable); the table lists all.
    const districtSeries: ChartSeries[] = sorted
      .slice(0, trendShow)
      .map((d, i) => ({
        label: d.name,
        data: d.values,
        color: TREND_COLORS[i % TREND_COLORS.length],
      }));

    const changeRows = sorted.map((r) => ({
      name: r.name,
      from: r.from,
      to: r.to,
      changeLabel: `${r.change >= 0 ? '↑ +' : '↓ '}${r.change}`,
    }));

    // Drill level follows the API (districts → blocks → schools).
    const level = trendDistResp?.result?.level ?? 'districts';
    const levelTitle =
      level === 'schools'
        ? 'Schools'
        : level === 'blocks'
          ? 'Blocks'
          : 'Districts';
    const schoolLeaf = level === 'schools';
    const handleTrendRowClick = (row: Record<string, unknown>) => {
      const name = String(row.name ?? '');
      if (level === 'districts') onDistrictChange?.(name);
      else if (level === 'blocks') onBlockChange?.(name);
      else selectSchool(name); // school = leaf → select the school
    };
    const changeColumns: ReportColumn[] = [
      {
        // 'link' at every level: district/block rows drill deeper, a school
        // row (the leaf) opens its School Detail view.
        key: 'name',
        label: drillLevelLabel(level),
        type: 'link',
      },
      { key: 'from', label: firstYear, type: 'number' },
      { key: 'to', label: lastYear, type: 'number' },
      { key: 'changeLabel', label: 'Change', type: 'text' },
    ];

    const allOpt = (label: string) => [{ label, value: 0 }];
    // No "All Years" entry — the dropdowns list only the windowed years
    // (2024-25-style floor … current year).
    const yearOpts = trendYearOptions;
    // FROM cannot be the current (latest) year — a range needs a later TO — so
    // drop the last option. TO cannot be the starting (earliest) year, so drop
    // the first option.
    const fromOpts = yearOpts.slice(0, -1);
    const toOpts = yearOpts.slice(1);
    // Defaults when the user hasn't picked yet: FROM = earliest, TO = current
    // (latest). Null until options load so the Dropdown never holds a value
    // that isn't one of its options.
    const defaultFrom = fromOpts[0]?.value ?? null;
    const defaultTo = toOpts[toOpts.length - 1]?.value ?? null;
    const hasOpt = (
      v: number | null,
      opts: { label: string; value: number }[]
    ) => v != null && opts.some((o) => o.value === v);
    const fromValue = hasOpt(trendFrom, fromOpts) ? trendFrom : defaultFrom;
    const toValue = hasOpt(trendTo, toOpts) ? trendTo : defaultTo;

    return (
      <>
        <div className="academic-scores__controls">
          <div className="academic-scores__left-group">
            <div className="academic-scores__ctrl">
              <span className="academic-scores__ctrl-label">FROM</span>
              <Dropdown
                value={fromValue}
                options={fromOpts}
                onChange={(e) => setTrendFrom(e.value || null)}
                className="academic-scores__dd academic-scores__dd--year"
                panelClassName="academic-scores__dd-panel"
                placeholder="—"
              />
            </div>
            <div className="academic-scores__ctrl">
              <span className="academic-scores__ctrl-label">TO</span>
              <Dropdown
                value={toValue}
                options={toOpts}
                onChange={(e) => setTrendTo(e.value || null)}
                className="academic-scores__dd academic-scores__dd--year"
                panelClassName="academic-scores__dd-panel"
                placeholder="—"
              />
            </div>
            <div className="academic-scores__ctrl">
              <span className="academic-scores__ctrl-label">METRIC</span>
              <Dropdown
                value={trendMetric}
                options={metricOptions}
                onChange={(e) => setTrendMetric(e.value)}
                className="academic-scores__dd"
                panelClassName="academic-scores__dd-panel"
              />
            </div>
            <div className="academic-scores__ctrl">
              <span className="academic-scores__ctrl-label">EXAM TYPE</span>
              <Dropdown
                value={trendExam ?? 0}
                options={[...allOpt('All Exam Types'), ...EXAM_TYPES]}
                onChange={(e) => setTrendExam(e.value || null)}
                className="academic-scores__dd"
                panelClassName="academic-scores__dd-panel"
              />
            </div>
            <div className="academic-scores__ctrl">
              <span className="academic-scores__ctrl-label">CLASS</span>
              <Dropdown
                value={trendClass ?? 0}
                options={[...allOpt('All Classes'), ...EXAM_CLASSES]}
                onChange={(e) => setTrendClass(e.value || null)}
                className="academic-scores__dd"
                panelClassName="academic-scores__dd-panel"
              />
            </div>
          </div>
          {/* Mode toggle + SHOW pushed to the right of the row. */}
          <div className="academic-scores__right-group">
            <div className="academic-scores__band-toggle" role="tablist">
              <button
                type="button"
                className={`academic-scores__band${trendMode === 'improved' ? ' is-active' : ''}`}
                onClick={() => setTrendMode('improved')}
              >
                <i className="pi pi-arrow-up-right" /> Most improved
              </button>
              <button
                type="button"
                className={`academic-scores__band${trendMode === 'decline' ? ' is-active' : ''}`}
                onClick={() => setTrendMode('decline')}
              >
                <i className="pi pi-arrow-down-right" /> Steepest decline
              </button>
            </div>
            <div className="academic-scores__ctrl">
              <span className="academic-scores__ctrl-label">SHOW</span>
              <Dropdown
                value={trendShow}
                options={TREND_SHOW_OPTIONS}
                onChange={(e) => setTrendShow(e.value)}
                className="academic-scores__dd academic-scores__dd--xs"
                panelClassName="academic-scores__dd-panel"
              />
            </div>
          </div>
        </div>
        {scopeNote}

        <SectionTitle
          icon="pi pi-chart-line"
          text={`Academic Trend — ${pageScopeName} ${firstYear} to ${lastYear}`}
        />

        <div className="mb-4">
          <AppChart
            type="line"
            title="Average Mark, Pass % and Compliance"
            icon="pi pi-chart-line"
            iconColor="#2f5fb0"
            // Toolbar icons: Refresh + Table View (defaults) plus Bar / Line
            // view switch (handled internally by AppChart's viewOverride) and
            // download.
            tools={[
              { icon: 'pi pi-refresh', label: 'Refresh' },
              { icon: 'pi pi-file', label: 'Table View' },
              { icon: 'pi pi-chart-bar', label: 'Bar Chart' },
              { icon: 'pi pi-chart-line', label: 'Line Chart' },
              { icon: 'pi pi-download', label: 'Download' },
            ]}
            labels={years}
            series={stateSeries}
            yTitle="Marks / %"
            yMax={120}
            height={320}
          />
        </div>

        <div className="mb-4">
          <AppChart
            type="line"
            title={`${trendMode === 'improved' ? 'Most Improved' : 'Steepest Decline'} — ${metricLabel} by ${levelTitle}`}
            titleExtra={
              <span className="academic-scores__count-badge">
                {sorted.length} total · {Math.min(trendShow, sorted.length)}{' '}
                shown
              </span>
            }
            icon="pi pi-share-alt"
            iconColor="#2f5fb0"
            labels={years}
            series={districtSeries}
            yTitle={metricLabel}
            yMax={120}
            // Refresh + Table View + Bar / Line view switch + download.
            tools={[
              { icon: 'pi pi-refresh', label: 'Refresh' },
              { icon: 'pi pi-file', label: 'Table View' },
              { icon: 'pi pi-chart-bar', label: 'Bar Chart' },
              { icon: 'pi pi-chart-line', label: 'Line Chart' },
              { icon: 'pi pi-download', label: 'Download' },
            ]}
            // Click a district/block line to drill one level deeper (districts
            // → blocks → schools), same as clicking a table row. Schools are
            // the leaf, so no drill there.
            onSeriesClick={
              schoolLeaf ? undefined : (name) => handleTrendRowClick({ name })
            }
            height={360}
          />
        </div>

        <div className="mb-4">
          <DrillBreadcrumb
            district={district}
            block={block}
            school={school}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
            onSchoolChange={onSchoolChange}
          />
          <ReportDataTable
            key={`trend-${level}-${district ?? ''}-${block ?? ''}`}
            title={`${levelTitle} — ${metricLabel} change, ${firstYear} to ${lastYear}`}
            hint={drillHint(level)}
            icon="pi pi-table"
            columns={
              level === 'schools' && changeRows.length === 1
                ? [
                    { ...changeColumns[0], type: 'text' },
                    ...changeColumns.slice(1),
                  ]
                : changeColumns
            }
            data={changeRows}
            searchPlaceholder={drillSearchPlaceholder(level)}
            exportFileName="academic-trend-change"
            rows={12}
            onRowClick={handleTrendRowClick}
          />
        </div>
      </>
    );
  };

  // ═════════════════════════════════════════════
  // COMPARISON
  // ═════════════════════════════════════════════
  const renderBetweenExams = () => {
    const labelA = sittingLabel(examA.cls, examA.exam);
    const labelB = sittingLabel(examB.cls, examB.exam);
    const boardA = isBoardExam(examA.cls, examA.exam);
    const boardB = isBoardExam(examB.cls, examB.exam);

    // Subject-level data has avg mark + pass % only (no compliance), so the
    // compliance metric falls back to the average mark there.
    const subjPass = comparisonMetric === 'pass';
    const subjMetricLabel = subjPass ? 'Pass %' : 'Average Mark';
    const subjSuffix = subjPass ? '%' : '';
    const subjA = (s: AcademicExamCompareSubject) =>
      num(subjPass ? s.a_pass_pct : s.a_average_mark);
    const subjB = (s: AcademicExamCompareSubject) =>
      num(subjPass ? s.b_pass_pct : s.b_average_mark);

    // ── A vs B summary ──
    const cmp = examCmpResp?.result;
    const sides = [cmp?.side_a, cmp?.side_b].map((s) => ({
      avg: num(s?.average_mark),
      pass: num(s?.pass_pct),
      compliance: num(s?.compliance),
      entered: num(s?.entered),
    }));
    const diff = {
      avg: num(cmp?.difference?.avg_mark_diff),
      pass: num(cmp?.difference?.pass_pct_diff),
      compliance: num(cmp?.difference?.compliance_diff),
    };
    const diffMetrics = [
      { key: 'avg', value: diff.avg, unit: '', label: 'avg mark' },
      { key: 'pass', value: diff.pass, unit: '%', label: 'pass' },
      {
        key: 'compliance',
        value: diff.compliance,
        unit: '%',
        label: 'compliance',
      },
    ];
    const headline =
      diffMetrics.find((m) => m.key === comparisonMetric) ?? diffMetrics[0];
    const secondary = diffMetrics.filter((m) => m.key !== headline.key);

    // ── Subjects shared by A and B ──
    const allSubjects = examCmpAllResp?.result?.subjects ?? [];
    const subjectOptions = allSubjects.map((s) => ({
      label: subjectLabel(s),
      value: Number(s.subject_id),
    }));
    const subjects = examSubject
      ? allSubjects.filter((s) => Number(s.subject_id) === examSubject)
      : allSubjects;
    const subjectNames = subjects.map(subjectLabel);
    const subjectSeries: ChartSeries[] = [
      { label: labelA, data: subjects.map(subjA), color: EXAM_COLORS.a },
      { label: labelB, data: subjects.map(subjB), color: EXAM_COLORS.b },
      {
        label: 'Difference',
        data: subjects.map((s) => r1(subjB(s) - subjA(s))),
        color: EXAM_COLORS.diff,
        asLine: true,
        useRightAxis: true,
        dashed: true,
      },
    ];
    const subjectColumns: ReportColumn[] = [
      { key: 'subject', label: 'Subject', type: 'text', bold: true },
      { key: 'a', label: labelA, type: 'number' },
      { key: 'b', label: labelB, type: 'number' },
      { key: 'diff', label: 'Avg Diff', type: 'delta' },
    ];
    const subjectRows = subjects.map((s) => ({
      subject: subjectLabel(s),
      a: subjA(s),
      b: subjB(s),
      diff: r1(subjB(s) - subjA(s)),
    }));

    // ── Exam sittings (exam type × class) with entered marks ──
    const sittings = (sittingsResp?.result?.rows ?? [])
      .map((r) => ({
        exam: Number(r.exam_type_id),
        cls: Number(r.class_id),
        avg: num(r.average_mark),
        pass: num(r.pass_pct),
        compliance: num(r.compliance),
        entered: num(r.entered),
      }))
      .filter((r) => r.entered > 0)
      .sort((a, b) => a.cls - b.cls || a.exam - b.exam);
    const boardCount = sittings.filter((r) =>
      isBoardExam(r.cls, r.exam)
    ).length;
    const chartSittings = sittingsClass
      ? sittings.filter((r) => r.cls === sittingsClass)
      : sittings;
    // Plotted series: Average Mark bars + Pass % line. Students Entered is
    // NOT plotted — it's added to the hover tooltip only (see
    // sittingsTooltip), so it informs without a third axis/series.
    const sittingsSeries: ChartSeries[] = [
      {
        label: 'Average Mark',
        data: chartSittings.map((r) => r.avg),
        color: EXAM_COLORS.a,
        barColors: chartSittings.map((r) =>
          isBoardExam(r.cls, r.exam) ? EXAM_COLORS.board : EXAM_COLORS.a
        ),
      },
      {
        label: 'Pass %',
        data: chartSittings.map((r) => r.pass),
        color: EXAM_COLORS.pass,
        asLine: true,
        // Share the 0–100 LEFT axis with Average Mark (no separate right axis).
      },
    ];

    // Hover tooltip rows per sitting: the two plotted metrics PLUS Students
    // Entered, so the count shows on hover without being drawn on the chart.
    const sittingsTooltip: Record<string, { label: string; value: string }[]> =
      Object.fromEntries(
        chartSittings.map((r) => [
          sittingLabel(r.cls, r.exam),
          [
            { label: 'Average Mark', value: `${r.avg}` },
            { label: 'Pass %', value: `${r.pass}%` },
            {
              label: 'Students Entered',
              value: Number(r.entered).toLocaleString('en-IN'),
            },
          ],
        ])
      );
    const sittingColumns: ReportColumn[] = [
      { key: 'examName', label: 'Exam Name', type: 'text', bold: true },
      { key: 'cls', label: 'Class', type: 'number' },
      { key: 'type', label: 'Type', type: 'badge', sortable: false },
      { key: 'avg', label: 'Avg Mark', type: 'number', bold: true },
      { key: 'passLabel', label: 'Pass %', type: 'text' },
      { key: 'complianceLabel', label: 'Compliance', type: 'text' },
      { key: 'entered', label: 'Entered', type: 'number' },
    ];
    const sittingRows = sittings.map((r) => ({
      key: `${r.exam}-${r.cls}`,
      examName: examName(r.exam),
      cls: r.cls,
      type: isBoardExam(r.cls, r.exam) ? 'Board' : 'EMIS Entry',
      avg: r.avg,
      passLabel: `${r.pass}%`,
      complianceLabel: `${r.compliance}%`,
      entered: r.entered,
    }));

    // ── Per exam type, across every class sitting it (weighted by entered) ──
    const perExam = EXAM_TYPES.map((e) => {
      const rows = sittings.filter((r) => r.exam === e.value);
      const entered = rows.reduce((t, r) => t + r.entered, 0);
      const w = (k: 'avg' | 'pass' | 'compliance') =>
        entered
          ? r1(rows.reduce((t, r) => t + r[k] * r.entered, 0) / entered)
          : 0;
      return {
        exam: e.label,
        avg: w('avg'),
        pass: w('pass'),
        compliance: w('compliance'),
        entered,
      };
    }).filter((e) => e.entered > 0);
    const baseline = perExam[0];
    const progressionSeries: ChartSeries[] = [
      {
        label: 'Average Mark',
        data: perExam.map((e) => e.avg),
        color: EXAM_COLORS.a,
      },
      {
        label: 'Pass %',
        data: perExam.map((e) => e.pass),
        color: EXAM_COLORS.pass,
        asLine: true,
        // Share the 0–100 LEFT axis (no separate right axis).
      },
      {
        label: 'Compliance %',
        data: perExam.map((e) => e.compliance),
        color: EXAM_COLORS.diff,
        asLine: true,
        dashed: true,
      },
    ];
    const examCmpColumns: ReportColumn[] = [
      { key: 'exam', label: 'Exam Type', type: 'text', bold: true },
      { key: 'avg', label: 'Avg Mark', type: 'number', bold: true },
      { key: 'vsBaseline', label: 'vs Baseline', type: 'delta' },
      { key: 'passLabel', label: 'Pass %', type: 'text' },
      { key: 'passDelta', label: 'Pass Δ', type: 'percent' },
      { key: 'complianceLabel', label: 'Compliance', type: 'text' },
      { key: 'entered', label: 'Students Entered', type: 'number' },
    ];
    const examCmpRows = perExam.map((e, i) => ({
      exam: i === 0 ? `${e.exam} (baseline)` : e.exam,
      avg: e.avg,
      vsBaseline: i === 0 ? null : r1(e.avg - baseline.avg),
      passLabel: `${e.pass}%`,
      passDelta: i === 0 ? null : r1(e.pass - baseline.pass),
      complianceLabel: `${e.compliance}%`,
      entered: e.entered,
    }));

    // ── Exam vs Subject (Exam A's class, all three exams) ──
    const qhySubjects = examQhyResp?.result?.subjects ?? [];
    const qaSubjects = examQaResp?.result?.subjects ?? [];
    const evsIds = Array.from(
      new Set([...qaSubjects, ...qhySubjects].map((s) => s.subject_id))
    );
    const findSub = (list: AcademicExamCompareSubject[], id: string) =>
      list.find((s) => s.subject_id === id);
    const evsLabels = evsIds.map((id) =>
      subjectLabel((findSub(qaSubjects, id) ?? findSub(qhySubjects, id))!)
    );
    const evsSeries: ChartSeries[] = [
      {
        label: 'Quarterly',
        data: evsIds.map((id) => {
          const s = findSub(qaSubjects, id) ?? findSub(qhySubjects, id);
          return s ? subjA(s) : 0;
        }),
        color: EXAM_COLORS.a,
      },
      {
        label: 'Half Yearly',
        data: evsIds.map((id) => {
          const s = findSub(qhySubjects, id);
          return s ? subjB(s) : 0;
        }),
        color: EXAM_COLORS.b,
      },
      {
        label: 'Annual',
        data: evsIds.map((id) => {
          const s = findSub(qaSubjects, id);
          return s ? subjB(s) : 0;
        }),
        color: EXAM_COLORS.diff,
      },
    ];

    // ── Improvement by district → block (district selected) → school
    // (block / school selected). Level comes from the API, else the header.
    const impLevelRaw = String(
      improvementResp?.result?.level ??
        (block || school ? 'schools' : district ? 'blocks' : 'districts')
    ).toLowerCase();
    const impLevel = impLevelRaw.startsWith('school')
      ? 'schools'
      : impLevelRaw.startsWith('block')
        ? 'blocks'
        : 'districts';
    const isDistrictLevel = impLevel === 'districts';
    const isSchoolLevel = impLevel === 'schools';
    const entityWord = isDistrictLevel
      ? 'district'
      : isSchoolLevel
        ? 'school'
        : 'block';
    const entityTitle =
      entityWord.charAt(0).toUpperCase() + entityWord.slice(1);
    // Schools are the leaf — no further drill.
    const canDrill = !isSchoolLevel;
    const improvementRows = improvementResp?.result?.rows ?? [];
    // Resolve an entity's display name from whatever field the improvement API
    // actually populates. It is NOT consistent across levels — some rows carry
    // `name`, others `school_name` / `block_name` / `district_name`, and a few
    // only an id — so every known key is tried before falling back to the
    // UDISE code, and only then to a blank. "Unknown" rows meant this lookup
    // was too narrow (it only tried name/block_name/school_name).
    const rowName = (r: Record<string, unknown>): string => {
      // District rows: prefer the master-list name matched by id.
      if (isDistrictLevel && r.district_id != null) {
        const byId = districtIdToNameCaution.get(String(r.district_id));
        if (byId) return byId;
      }
      const candidates = [
        r.name,
        r.school_name,
        r.block_name,
        r.district_name,
        r.entity_name,
        r.udise_code != null ? `UDISE ${r.udise_code}` : undefined,
      ];
      const hit = candidates.find((v) => v != null && String(v).trim() !== '');
      return hit != null ? String(hit) : '';
    };

    const improvement = (
      isSchoolLevel
        ? onlySelectedSchool(improvementRows, (r) => ({
            id: r.school_id,
            name: r.name ?? r.school_name,
          }))
        : improvementRows
    )
      .map((r) => {
        const a = num(r.a_average_mark);
        const b = num(r.b_average_mark);
        const both = a > 0 && b > 0;
        return {
          name: rowName(r as Record<string, unknown>),
          a,
          b,
          // A gain is only meaningful when both exams have marks; otherwise
          // the API's gain is just -A (or +B), so it's left null.
          gain: both ? r1(num(r.gain)) : null,
        };
      })
      .sort((x, y) => (y.gain ?? -Infinity) - (x.gain ?? -Infinity));
    // Plot any entity that has at least ONE of the two exam marks. Previously
    // this required a computable `gain` (BOTH marks present), so if the API
    // returned 0 for one exam on every row — common when exam B hasn't been
    // conducted yet — every row was filtered out and the chart was blank. The
    // Gain line still carries null where it can't be computed and skips it.
    const improvementPlotted = improvement.filter((r) => r.a > 0 || r.b > 0);
    // "Missing" = rows with neither mark (nothing to show at all).
    const improvementMissing = improvement.length - improvementPlotted.length;
    const improvementSeries: ChartSeries[] = [
      {
        label: examName(examA.exam),
        data: improvementPlotted.map((r) => r.a),
        color: EXAM_COLORS.a,
      },
      {
        label: examName(examB.exam),
        data: improvementPlotted.map((r) => r.b),
        color: EXAM_COLORS.b,
      },
      {
        label: 'Gain',
        // null (not 0) where gain can't be computed, so the points-only line
        // simply omits those entities rather than planting a false zero.
        data: improvementPlotted.map((r) =>
          r.gain == null ? (null as unknown as number) : r.gain
        ),
        color: EXAM_COLORS.diff,
        asLine: true,
        pointsOnly: true,
      },
    ];
    const improvementColumns: ReportColumn[] = [
      {
        // 'link' at every level: district/block rows drill deeper, a school
        // row (the leaf) opens its School Detail view.
        key: 'name',
        label: entityTitle,
        type: 'link',
      },
      { key: 'a', label: examName(examA.exam), type: 'number' },
      { key: 'b', label: examName(examB.exam), type: 'number', bold: true },
      { key: 'gain', label: 'Gain', type: 'delta' },
    ];
    // Same handlers as the header dropdowns, so the header and every
    // section of this tab re-scope together.
    const drillTo = (name: string) => {
      if (!name) return;
      if (isDistrictLevel) onDistrictChange?.(name);
      else if (impLevel === 'blocks') onBlockChange?.(name);
      else selectSchool(name); // school = leaf → select the school
    };
    const classRange =
      examA.cls === examB.cls
        ? `class ${examA.cls}`
        : `classes ${Math.min(examA.cls, examB.cls)}-${Math.max(examA.cls, examB.cls)}`;

    const renderSide = (
      side: 'a' | 'b',
      pick: ExamPick,
      setPick: (p: ExamPick) => void,
      board: boolean
    ) => (
      <div
        className={`academic-scores__exam-side academic-scores__exam-side--${side}`}
      >
        <span className="academic-scores__exam-tag">
          EXAM {side.toUpperCase()}
        </span>
        <div className="academic-scores__ctrl">
          <span className="academic-scores__ctrl-label">CLASS</span>
          <Dropdown
            value={pick.cls}
            options={EXAM_CLASSES}
            onChange={(e) => setPick({ ...pick, cls: e.value })}
            className="academic-scores__dd academic-scores__dd--sm"
          />
        </div>
        <div className="academic-scores__ctrl">
          <span className="academic-scores__ctrl-label">EXAM</span>
          <Dropdown
            value={pick.exam}
            options={EXAM_TYPES}
            onChange={(e) => setPick({ ...pick, exam: e.value })}
            className="academic-scores__dd academic-scores__dd--sm"
          />
        </div>
        {board && <span className="academic-scores__board-pill">Board</span>}
      </div>
    );

    const renderSideCard = (
      side: 'a' | 'b',
      label: string,
      s: (typeof sides)[number]
    ) => (
      <div
        className={`academic-scores__side-card academic-scores__side-card--${side}`}
      >
        <h6 className="academic-scores__side-title">{label}</h6>
        {examCmpLoading ? (
          <div className="academic-scores__side-loading">
            <i className="pi pi-spin pi-spinner" />
          </div>
        ) : (
          <dl className="academic-scores__side-stats">
            <dt>Avg Mark</dt>
            <dd>{s.avg}</dd>
            <dt>Pass %</dt>
            <dd>{s.pass}%</dd>
            <dt>Compliance</dt>
            <dd>{s.compliance}%</dd>
            <dt>Entered</dt>
            <dd>{fmtIndian(s.entered)}</dd>
          </dl>
        )}
      </div>
    );

    return (
      <>
        <DrillBreadcrumb
          district={district}
          block={block}
          school={school}
          onDistrictChange={onDistrictChange}
          onBlockChange={onBlockChange}
          onSchoolChange={onSchoolChange}
        />
        <div className="academic-scores__exam-picker mb-3">
          {renderSide('a', examA, setExamA, boardA)}
          <span className="academic-scores__vs">vs</span>
          {renderSide('b', examB, setExamB, boardB)}
          <div className="academic-scores__ctrl academic-scores__ml-auto">
            <span className="academic-scores__ctrl-label">
              SUBJECT (OPTIONAL)
            </span>
            <Dropdown
              value={examSubject}
              options={subjectOptions}
              onChange={(e) => setExamSubject(e.value ?? null)}
              placeholder="All subjects"
              showClear={examSubject != null}
              emptyMessage="No shared subjects"
              className="academic-scores__dd"
            />
          </div>
        </div>

        {boardA !== boardB && (
          <div className="academic-scores__banner mb-3">
            <i className="pi pi-exclamation-triangle" />
            <span>
              You are comparing a board exam (class{' '}
              {boardA ? examA.cls : examB.cls}) with an internal EMIS exam —
              these are not like-for-like assessments.
            </span>
          </div>
        )}
        {examA.cls === examB.cls && examA.exam === examB.exam && (
          <div className="academic-scores__banner mb-3">
            <i className="pi pi-info-circle" />
            <span>
              Exam A and Exam B are the same sitting — pick a different class or
              exam to compare.
            </span>
          </div>
        )}

        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-5">
            {renderSideCard('a', labelA, sides[0])}
          </div>
          <div className="col-12 col-lg-2">
            <div className="academic-scores__diff-card">
              <span className="academic-scores__diff-label">
                DIFFERENCE (B − A)
              </span>
              <span
                className={`academic-scores__diff-value ${toneOf(headline.value)}`}
              >
                {headline.value !== 0 && (
                  <i
                    className={
                      headline.value > 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down'
                    }
                  />
                )}
                {signed(headline.value, headline.unit)}
              </span>
              <span className="academic-scores__diff-unit">
                {headline.label}
              </span>
              {secondary.map((m) => (
                <span
                  key={m.key}
                  className={`academic-scores__diff-sub ${toneOf(m.value)}`}
                >
                  {signed(m.value, m.unit)} {m.label}
                </span>
              ))}
            </div>
          </div>
          <div className="col-12 col-lg-5">
            {renderSideCard('b', labelB, sides[1])}
          </div>
        </div>

        <div className="mb-4">
          <AppChart
            type="combo"
            title={`${labelA} vs ${labelB}`}
            titleSuffix={`${
              examSubject
                ? (subjectNames[0] ?? 'subject')
                : 'all shared subjects'
            } · dashed line = difference (B − A)`}
            icon="pi pi-arrows-h"
            iconColor="#8b1e3f"
            labels={subjectNames}
            series={subjectSeries}
            yTitle={subjMetricLabel}
            y1Title="Difference"
            barValueFormat={(v) => `${v}${subjSuffix}`}
            loading={examCmpAllLoading}
            emptyMessage={
              !examCmpAllLoading && subjects.length === 0
                ? 'No subjects shared by these two exams'
                : undefined
            }
            height={300}
          />
        </div>

        <div className="mb-4">
          <ReportDataTable
            key={`exam-subjects-${labelA}-${labelB}-${comparisonMetric}`}
            title="Subject Comparison"
            hint={`${subjectRows.length} subjects · ${subjMetricLabel}`}
            icon="pi pi-table"
            columns={subjectColumns}
            data={subjectRows}
            loading={examCmpAllLoading}
            searchable={false}
            paginated={subjectRows.length > 12}
            exportFileName="academic-exam-subject-comparison"
          />
        </div>

        <div className="row g-3 mb-4">
          {perExam.map((e, i) => {
            const d = r1(e.avg - baseline.avg);
            return (
              <div className="col-12 col-md-4" key={e.exam}>
                <div
                  className={`academic-scores__exam-tile${i === 0 ? ' is-baseline' : ''}`}
                >
                  <span className="academic-scores__exam-tile-value">
                    {e.avg}
                  </span>
                  <span className="academic-scores__exam-tile-label">
                    {e.exam.toUpperCase()} · AVG MARK
                  </span>
                  <span className="academic-scores__exam-tile-sub">
                    Pass {e.pass}%{' '}
                    {i === 0 ? (
                      '(baseline)'
                    ) : (
                      <strong className={toneOf(d)}>
                        ({signed(d)} vs {baseline.exam})
                      </strong>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-6">
            <AppChart
              type="bar"
              title={`Exam vs Subject — Class ${examA.cls}`}
              titleSuffix={`${subjMetricLabel} · ${pageScopeName}`}
              icon="pi pi-chart-bar"
              iconColor="#8b1e3f"
              labels={evsLabels}
              series={evsSeries}
              yTitle={subjMetricLabel}
              yMax={120}
              barValueFormat={(v) => `${v}${subjSuffix}`}
              loading={examQhyLoading || examQaLoading}
              height={300}
            />
          </div>
          <div className="col-12 col-lg-6">
            <AppChart
              type="combo"
              title={`Exam Progression — ${pageScopeName}`}
              titleSuffix="all classes"
              icon="pi pi-sitemap"
              iconColor="#8b1e3f"
              labels={perExam.map((e) => e.exam)}
              series={progressionSeries}
              yTitle="Marks / %"
              yMax={120}
              barValueFormat={(v) => `${v}`}
              loading={sittingsLoading}
              height={300}
              tools={[
                { icon: 'pi pi-refresh', label: 'Refresh' },
                { icon: 'pi pi-file', label: 'Table View' },
                { icon: 'pi pi-chart-bar', label: 'Bar view' },
                { icon: 'pi pi-download', label: 'Export' },
              ]}
            />
          </div>
        </div>

        <div className="mb-4">
          <AppChart
            type="combo"
            title="Exam Sittings — name & class"
            titleSuffix="maroon = board exam (class 10 & 12, Annual only)"
            icon="pi pi-clipboard"
            iconColor="#8b1e3f"
            headerRight={
              <Dropdown
                value={sittingsClass}
                options={EXAM_CLASSES}
                onChange={(e) => setSittingsClass(e.value ?? null)}
                placeholder="All classes"
                showClear={sittingsClass != null}
                className="academic-scores__dd academic-scores__dd--sm"
              />
            }
            labels={chartSittings.map((r) => sittingLabel(r.cls, r.exam))}
            series={sittingsSeries}
            // Students Entered rides along in the hover only (not plotted).
            tooltipDetails={sittingsTooltip}
            yTitle="Marks / %"
            yMax={120}
            barValueFormat={(v) => `${v}`}
            loading={sittingsLoading}
            autoHeight={false}
            height={340}
            tools={[
              { icon: 'pi pi-refresh', label: 'Refresh' },
              { icon: 'pi pi-file', label: 'Table View' },
              { icon: 'pi pi-chart-bar', label: 'Bar view' },
              { icon: 'pi pi-download', label: 'Export' },
            ]}
          />
        </div>

        <div className="mb-4">
          <ReportDataTable
            title="Exam Sitting Detail"
            hint={`${sittingRows.length} sittings · ${boardCount} board`}
            icon="pi pi-table"
            columns={sittingColumns}
            data={sittingRows}
            dataKey="key"
            loading={sittingsLoading}
            searchPlaceholder="Search exam or class..."
            exportFileName="academic-exam-sittings"
            rows={12}
          />
        </div>

        <div className="mb-4">
          <ReportDataTable
            title={`Exam Comparison — ${pageScopeName}`}
            hint={
              baseline ? `deltas measured against ${baseline.exam}` : undefined
            }
            icon="pi pi-table"
            columns={examCmpColumns}
            data={examCmpRows}
            loading={sittingsLoading}
            searchable={false}
            paginated={false}
            exportFileName="academic-exam-comparison"
          />
        </div>

        <div className="mb-4">
          <AppChart
            type="horizontalBar"
            title={`Which ${entityWord}s improve most from ${examName(examA.exam)} to ${examName(examB.exam)}?`}
            titleSuffix={`${classRange}${
              canDrill ? ` · click a bar to drill into the ${entityWord}` : ''
            }${
              improvementMissing
                ? ` · ${improvementMissing} with no marks in either exam (see table)`
                : ''
            }`}
            icon="pi pi-sort-amount-down"
            iconColor="#8b1e3f"
            labels={improvementPlotted.map((r) => r.name)}
            series={improvementSeries}
            yTitle="Average Mark"
            yMax={120}
            loading={improvementLoading}
            emptyMessage={
              !improvementLoading && improvementPlotted.length === 0
                ? `No ${entityWord} has marks for ${examName(examA.exam)} or ${examName(examB.exam)}`
                : undefined
            }
            onBarClick={canDrill ? (_i, name) => drillTo(name) : undefined}
            height={360}
          />
        </div>

        <div className="mb-4">
          {/* Repeated next to the grid that drills — the breadcrumb at the top
              of this sub-tab is above the exam pickers and chart. */}
          <DrillBreadcrumb
            district={district}
            block={block}
            school={school}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
            onSchoolChange={onSchoolChange}
          />
          <ReportDataTable
            key={`improvement-${impLevel}-${examA.exam}-${examB.exam}`}
            title={`${entityTitle} Exam Improvement`}
            hint={canDrill ? drillHint(impLevel) : classRange}
            icon="pi pi-table"
            // columns={improvementColumns}
            columns={
              impLevel === 'schools' && improvement.length === 1
                ? [
                    { ...improvementColumns[0], type: 'text' },
                    ...improvementColumns.slice(1),
                  ]
                : improvementColumns
            }
            data={improvement}
            dataKey="name"
            loading={improvementLoading}
            searchPlaceholder={`Search ${entityWord}...`}
            exportFileName="academic-exam-improvement"
            rows={12}
            onRowClick={(row) => drillTo(String(row.name))}
          />
        </div>
      </>
    );
  };

  const renderComparison = () => {
    const isDistricts = comparisonMode === 'districts';

    return (
      <>
        <div className="academic-scores__controls">
          {/* {yearChip} */}
          <div className="academic-scores__ctrl">
            <span className="academic-scores__ctrl-label">METRIC</span>
            <Dropdown
              value={comparisonMetric}
              options={metricOptions}
              onChange={(e) => setComparisonMetric(e.value)}
              className="academic-scores__dd"
            />
          </div>
          {scopeNote}
        </div>

        <SectionTitle
          icon="pi pi-arrows-h"
          text="Academic Comparison"
          year={year}
        />

        <div className="academic-scores__band-toggle mb-3" role="tablist">
          <button
            type="button"
            className={`academic-scores__band${isDistricts ? ' is-active' : ''}`}
            onClick={() => setComparisonMode('districts')}
          >
            <i className="pi pi-map-marker" /> Between Districts
          </button>
          <button
            type="button"
            className={`academic-scores__band${!isDistricts ? ' is-active' : ''}`}
            onClick={() => setComparisonMode('exams')}
          >
            <i className="pi pi-book" /> Between Exams
          </button>
        </div>

        {isDistricts ? (
          <HeatmapTable
            title={`Subject vs ${subjectCmpLevelNoun} Heatmap — ${
              subjectCmp?.metric_title ?? 'Average Mark'
            }`}
            hint="spot which subject is weak, and where"
            icon="pi pi-th-large"
            columns={
              subjectCmpColumns.length
                ? subjectCmpColumns
                : COMPARISON_HEATMAP.subjects
            }
            // Only fall back to the static mock before any real response has
            // arrived. Once `subjectCmp` has loaded, an empty `subjectCmpRows`
            // is a genuine "no data for this selection" result (e.g. a
            // selected school has no comparison data) — showing the mock's
            // unrelated, unscoped district list there would be misleading.
            rows={
              subjectCmpRows.length
                ? subjectCmpRows
                : subjectCmp
                  ? []
                  : COMPARISON_HEATMAP.rows
            }
            // Fixed colour domain per metric so the red→yellow→green gradient
            // maps to a MEANINGFUL range instead of the data's own min/max.
            // Without this, values clustered in a narrow band (e.g. compliance
            // 2.1–2.7) all painted the same green and the hover gave no sense
            // of where a cell sat on the scale.
            min={heatmapDomain.min}
            max={heatmapDomain.max}
            valueSuffix={heatmapDomain.suffix}
            decimals={1}
            // Row names are clickable — drills district → block → school,
            // same as the ranked bar chart underneath.
            onRowClick={subjectCmpDrill}
            exportFileName="subject-vs-district-heatmap"
            tools={[
              { icon: 'pi pi-refresh', label: 'Refresh' },
              { icon: 'pi pi-file', label: 'Table View' },
              { icon: 'pi pi-download', label: 'Export' },
            ]}
          />
        ) : (
          renderBetweenExams()
        )}

        {/* Ranked bar chart — overall metric per entity, best first. Only on
            the Between Districts view; its data is the subject-comparison
            rows' overall value. */}
        {isDistricts && rankedEntities.length > 0 && (
          <div className="mb-4 mt-4">
            <AppChart
              type="horizontalBar"
              title={`${rankedMetricLabel} ranked`}
              titleSuffix={
                rankedCanDrill
                  ? `click a bar to drill into a ${rankedLevelNoun.toLowerCase()}`
                  : undefined
              }
              icon="pi pi-chart-bar"
              iconColor="#2f5fb0"
              labels={rankedLabels}
              series={rankedSeries}
              loading={rankedLoading}
              xTitle={rankedMetricLabel}
              xTickSuffix={rankedSuffix}
              barValueFormat={(v) => `${v}${rankedSuffix}`}
              averageLine={rankedAverage}
              tooltipDetails={Object.fromEntries(
                rankedEntities.map((e) => [
                  e.name,
                  [
                    {
                      label: rankedMetricLabel,
                      value: `${e.value}${rankedSuffix}`,
                    },
                  ],
                ])
              )}
              onBarClick={
                rankedCanDrill ? (_i, label) => rankedDrill(label) : undefined
              }
              height={420}
              tools={[
                { icon: 'pi pi-refresh', label: 'Refresh' },
                { icon: 'pi pi-file', label: 'Table View' },
                { icon: 'pi pi-chart-bar', label: 'Bar view' },
                { icon: 'pi pi-download', label: 'Export' },
              ]}
            />
          </div>
        )}
      </>
    );
  };

  // ═════════════════════════════════════════════
  // ALARMING
  // ═════════════════════════════════════════════
  const renderAlarming = () => {
    const alertsSeries: ChartSeries[] = [
      { label: 'Districts', data: ALERTS_BY_CATEGORY.counts, color: '#e8a33d' },
    ];

    const scatterGroups: ScatterGroup[] = Object.keys(RISK_COLORS)
      .map((g) => ({
        label: RISK_LABELS[g],
        color: RISK_COLORS[g],
        points: RISK_POINTS.filter((p) => p.group === g).map((p) => ({
          x: p.compliance,
          y: p.passPct,
          label: p.name,
        })),
      }))
      .filter((grp) => grp.points.length > 0);

    const alertColumns: ReportColumn[] = [
      { key: 'severity', label: 'Severity', type: 'text' },
      // Only a link (and so only chevroned) when a drill handler is wired.
      {
        key: 'district',
        label: 'District',
        type: onDistrictChange ? 'link' : 'text',
      },
      { key: 'alert', label: 'Alert', type: 'text' },
      { key: 'measured', label: 'Measured', type: 'text' },
    ];
    // Row click → drill into that district (and sync the header dropdown).
    const alertDrill = onDistrictChange
      ? (row: Record<string, unknown>) => {
          const name = String(row.district ?? '');
          if (name) onDistrictChange(name);
        }
      : undefined;

    return (
      <>
        <div className="academic-scores__controls justify-content-end">
          {/* {yearChip} */}
          {scopeNote}
        </div>

        <SectionTitle
          icon="pi pi-exclamation-triangle"
          text={`Academic Alarming Indicators (${ALARMING_KPIS.totalAlerts})`}
          year={year}
        />

        <div className="row g-3 mb-4">
          <div className="col-6 col-lg-3">
            <StatTile
              className="ac-tile ac-tile--danger"
              value={`${ALARMING_KPIS.highSeverity}`}
              label="High Severity"
            />
          </div>
          <div className="col-6 col-lg-3">
            <StatTile
              className="ac-tile ac-tile--warning"
              value={`${ALARMING_KPIS.mediumSeverity}`}
              label="Medium Severity"
            />
          </div>
          <div className="col-6 col-lg-3">
            <StatTile
              className="ac-tile ac-tile--navy"
              value={`${ALARMING_KPIS.totalAlerts}`}
              label="Total Alerts"
            />
          </div>
          <div className="col-6 col-lg-3">
            <StatTile
              className="ac-tile ac-tile--muted"
              value={`${ALARMING_KPIS.districtsFlagged}`}
              label="Districts Flagged"
            />
          </div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-5">
            <AppChart
              type="bar"
              title="Alerts by Category"
              icon="pi pi-chart-bar"
              iconColor="#e8a33d"
              labels={ALERTS_BY_CATEGORY.categories}
              series={alertsSeries}
              yTitle="Districts"
              tools={chartToolsNoTable}
              height={320}
            />
          </div>
          <div className="col-12 col-lg-7">
            <AppChart
              type="scatter"
              title="Risk Quadrant — Compliance vs Pass %"
              titleSuffix="bottom-left = under-reporting AND under-performing"
              icon="pi pi-compass"
              iconColor="#2f5fb0"
              scatterGroups={scatterGroups}
              xTitle="Data Compliance %"
              xTickSuffix="%"
              yTitle="Pass %"
              yTickSuffix="%"
              tools={chartToolsNoTable}
              height={320}
            />
          </div>
        </div>

        <div className="academic-scores__chips mb-3">
          <button
            type="button"
            className={`academic-scores__chip${alarmFilter === 'all' ? ' is-active' : ''}`}
            onClick={() => setAlarmFilter('all')}
          >
            All ({ALERT_DETAILS.length})
          </button>
          <button
            type="button"
            className={`academic-scores__chip${alarmFilter === 'compliance' ? ' is-active' : ''}`}
            onClick={() => setAlarmFilter('compliance')}
          >
            Low Data Compliance ({ALERT_DETAILS.length})
          </button>
        </div>

        <div className="mb-4">
          <DrillBreadcrumb
            district={district}
            block={block}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
          />
          <ReportDataTable
            title="Academic Alert Details"
            hint={`thresholds: pass < 70% · avg mark < 50 · compliance < 85% · any subject < 40 ${
              alertDrill ? drillHint('district') : ''
            }`.trim()}
            icon="pi pi-list"
            columns={alertColumns}
            data={ALERT_DETAILS}
            searchPlaceholder={drillSearchPlaceholder('district')}
            exportFileName="academic-alerts"
            rows={15}
            rowsPerPageOptions={[15, 30]}
            onRowClick={alertDrill}
          />
        </div>
      </>
    );
  };

  // ═════════════════════════════════════════════
  // CAUTION
  // ═════════════════════════════════════════════
  const renderCaution = () => {
    const cards = compCardsResp?.result ?? {};
    const list = compListResp?.result ?? [];
    const totalFlagged = ACADEMIC_CAUTION_CARDS.reduce(
      (t, c) => t + (Number(cards[c.key]) || 0),
      0
    );

    const schoolColumns: ReportColumn[] = [
      { key: 'district', label: 'District', type: 'text' },
      { key: 'block', label: 'Block', type: 'text' },
      // Schools are the LEAF of the drill (district → block → school), so this
      // column is plain text — no clickable arrow promising a deeper level
      // that doesn't exist. The UDISE is appended into the name, so there's no
      // separate UDISE column.
      { key: 'school', label: 'School', type: 'link' },
      { key: 'management', label: 'Management', type: 'muted' },
      { key: 'students', label: 'Students', type: 'number' },
      { key: 'entered', label: 'Entered', type: 'number' },
      { key: 'finding', label: 'Finding', type: 'finding' },
    ];
    const detailRows = onlySelectedSchool(list, (r) => ({
      id: r.school_id,
      name: r.school_name,
    })).map((r) => ({
      district:
        districtIdToNameCaution.get(String(r.district_name)) ??
        String(r.district_name ?? ''),
      block: r.block_name ?? '',
      school: withUdise(r.school_name, r.udise_code),
      management: r.management ?? '',
      students: Number(r.students) || 0,
      entered: Number(r.entered) || 0,
      finding: r.finding ?? '',
    }));

    const checkColumns: ReportColumn[] = [
      { key: 'label', label: 'Check', type: 'link' },
      { key: 'count', label: 'Schools', type: 'number', bold: true },
      { key: 'meaning', label: 'What It Means', type: 'muted' },
    ];
    const checkRows = ACADEMIC_CAUTION_CARDS.map((c) => ({
      key: c.key,
      label: c.label,
      // Picked up by ReportDataTable's 'link' cell renderer, which draws this
      // icon before the check name (same icon as the tile above it).
      icon: c.icon,
      count: Number(cards[c.key]) || 0,
      meaning: c.meaning,
    }));

    return (
      <>
        <div className="academic-scores__controls justify-content-end">
          {scopeNote}
        </div>

        <SectionTitle
          icon="pi pi-info-circle"
          text={`Caution — ${pageScopeName}`}
          suffix={`${totalFlagged.toLocaleString('en-IN')} schools flagged`}
        />

        <div className="academic-scores__banner mb-3">
          <i className="pi pi-exclamation-triangle" />
          <span>
            Schools failing a hard mark-entry / compliance check. Pick a check
            to list the flagged schools; every list downloads in full.
          </span>
        </div>

        <div className="row g-3 mb-4">
          {ACADEMIC_CAUTION_CARDS.map((c) => (
            <div className="col-6 col-lg-3" key={c.key}>
              <button
                type="button"
                className={`academic-scores__caution-card${cautionCheck === c.key ? ' is-active' : ''}`}
                onClick={() => setCautionCheck(c.key)}
              >
                <span
                  className="academic-scores__caution-icon"
                  style={{ color: c.accent }}
                >
                  <i className={c.icon} />
                </span>
                <span className="academic-scores__caution-count">
                  {(Number(cards[c.key]) || 0).toLocaleString('en-IN')}
                </span>
                <span className="academic-scores__caution-label">
                  {c.label}
                </span>
                <span className="academic-scores__caution-meaning">
                  {c.meaning}
                </span>
              </button>
            </div>
          ))}
        </div>

        <div className="mb-4">
          <DrillBreadcrumb
            district={district}
            block={block}
            school={school}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
            onSchoolChange={onSchoolChange}
          />
          <ReportDataTable
            key={activeCautionCard.key}
            title={activeCautionCard.label}
            hint={activeCautionCard.meaning}
            suffix={`(${detailRows.length} Schools)`}
            icon="pi pi-flag"
            columns={schoolColumns}
            data={detailRows as unknown as Record<string, unknown>[]}
            loading={compListLoading}
            dataKey="udise"
            searchPlaceholder="Search school, district, block or UDISE..."
            exportFileName={`academic-caution-${activeCautionCard.key}`}
            rows={15}
            rowsPerPageOptions={[15, 30, 45]}
            // Rows ARE schools (the leaf) — clicking one jumps straight to
            // that school, syncing the header District/Block/School dropdowns
            // too (the row already carries its own district/block names) so
            // the breadcrumb above this grid appears and matches the pick.
            onRowClick={(row) =>
              onGeoSelect?.({
                district: String(row.district ?? '') || null,
                block: String(row.block ?? '') || null,
                school:
                  String(row.school ?? '')
                    .replace(/\s*\(\d[\d/]*\)\s*$/, '')
                    .trim() || null,
              })
            }
          />
        </div>

        <div className="mb-4 cautions-grid">
          <ReportDataTable
            title={`All Caution Checks — ${pageScopeName}`}
            hint="counts across every check"
            icon="pi pi-list"
            columns={checkColumns}
            data={checkRows as unknown as Record<string, unknown>[]}
            loading={compCardsLoading}
            searchable={false}
            exportFileName="academic-caution-checks"
            rows={10}
            onRowClick={(row) => setCautionCheck(String(row.key))}
          />
        </div>
      </>
    );
  };

  // ═════════════════════════════════════════════
  // Root
  // ═════════════════════════════════════════════
  const bands: { label: string; value: ClassBand; icon: string }[] = [
    { label: 'Class 6-10 (5 subjects)', value: '6-10', icon: 'pi pi-book' },
    { label: 'Class 11-12 (streams)', value: '11-12', icon: 'pi pi-id-card' },
  ];

  return (
    <section className="academic-scores p-3">
      {/* Numbers tab keeps the band toggle in its control row. */}
      {activeSubTab === 'numbers' && (
        <div className="academic-scores__controls">
          {/* {yearChip} */}
          {/* {yearChip} */}
          <div className="academic-scores__band-toggle" role="tablist">
            {bands.map((b) => (
              <button
                key={b.value}
                type="button"
                role="tab"
                aria-selected={band === b.value}
                className={`academic-scores__band${band === b.value ? ' is-active' : ''}`}
                onClick={() => setBand(b.value)}
              >
                <i className={b.icon} />
                {b.label}
              </button>
            ))}
          </div>
          {scopeNote}
        </div>
      )}

      {activeSubTab === 'numbers' && renderNumbers()}
      {activeSubTab === 'trend' && renderTrend()}
      {activeSubTab === 'comparison' && renderComparison()}
      {/* {activeSubTab === 'alarming' && renderAlarming()} */}
      {activeSubTab === 'caution' && renderCaution()}
    </section>
  );
};

// ─────────────────────────────────────────────
// Small presentational helpers
// ─────────────────────────────────────────────

const SectionTitle: React.FC<{
  icon: string;
  text: string;
  year?: string;
  suffix?: string;
}> = ({ icon, text, year, suffix }) => (
  <h5 className="academic-scores__heading">
    <i className={icon} />
    {text} {year && <span>{year}</span>}
    {suffix && <span>{suffix}</span>}
  </h5>
);

const ControlChip: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="academic-scores__ctrl">
    <span className="academic-scores__ctrl-label">{label}</span>
    <span className="academic-scores__ctrl-value">{value}</span>
  </div>
);

export default AcademicScores;
